import { sourcedSerieA2022Players } from "./sourcedSerieA2022";

export type Role = "POR" | "DIF" | "CEN" | "ATT";

export type PositionTag =
  | "portiere"
  | "centrale"
  | "terzino"
  | "braccetto"
  | "quinto"
  | "mediano"
  | "mezzala"
  | "regista"
  | "esterno"
  | "ala"
  | "trequartista"
  | "seconda-punta"
  | "centravanti";

export type Era = "anni70" | "anni80" | "anni90" | "anni2000" | "anni2010" | "moderna";

export type Club =
  | "Atalanta"
  | "Bari"
  | "Bologna"
  | "Brescia"
  | "Cagliari"
  | "Chievo"
  | "Empoli"
  | "Fiorentina"
  | "Foggia"
  | "Inter"
  | "Juventus"
  | "Lazio"
  | "Lecce"
  | "Milan"
  | "Napoli"
  | "Palermo"
  | "Parma"
  | "Perugia"
  | "Piacenza"
  | "Reggina"
  | "Roma"
  | "Salernitana"
  | "Sampdoria"
  | "Torino"
  | "Udinese"
  | "Venezia"
  | "Verona"
  | "Vicenza";

export interface PlayerSeason {
  id: string;
  name: string;
  club: Club;
  season: string;
  era: Era;
  roles: Role[];
  positionTags?: PositionTag[];
  country: string;
  score: number;
  goals?: number;
  assists?: number;
  appearances?: number;
  tag: string;
  note: string;
  starts?: number;
  statbunkerSubRaw?: number;
  source?: {
    name: "StatBunker" | "FBref" | "Manual cross-check";
    url: string;
    retrievedAt: string;
    table: string;
    statFields: string[];
    roleMapping?: string;
  };
  evidence?: {
    name:
      | "Lazio Stories"
      | "RSSSF"
      | "ESPN"
      | "FootballSquads"
      | "JuveFC"
      | "TopScorers.Club"
      | "Inter"
      | "worldfootball.net"
      | "StatsCrew"
      | "BDFutbol"
      | "Transfermarkt"
      | "AC Milan"
      | "Manual cross-check";
    url: string;
    retrievedAt: string;
    fields: string[];
    note?: string;
  }[];
}

export const dataNotice =
  "Modalita miste: draft moderno post-2010 facile/hard e Nostalgia storica curate a mano, piu una slice 2022-23 con presenze, titolarita e gol da StatBunker. Ruoli e microcopy restano metadata di gameplay.";

const evidenceRefs = {
  lazioStories1999: {
    name: "Lazio Stories",
    url: "https://www.laziostories.com/post/1999-00-complete-player-statistics",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A goals", "all-competition assists notes"],
    note: "Historical Lazio 1999-00 appearances/goals table.",
  },
  signoriRsssf: {
    name: "RSSSF",
    url: "https://www.rsssf.org/players/signoridata.html",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A goals"],
    note: "Giuseppe Signori Serie A season line.",
  },
  manualLazio1992: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1992%E2%80%9393_SS_Lazio_season",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league table", "Serie A appearances", "Serie A goals", "top scorer"],
    note: "Lazio 1992-93 fifth-place season and player-statistics table.",
  },
  espnSerieA2011: {
    name: "ESPN",
    url: "https://www.espn.com/soccer/stats/_/league/ITA.1/season/2011",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A goals", "Serie A assists"],
    note: "2011-12 Serie A scorer and assist tables.",
  },
  juveFc2015: {
    name: "JuveFC",
    url: "https://www.juvefc.com/juventus-statistics-201516/",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A goals", "Serie A assists"],
    note: "Juventus 2015-16 squad stat table by role.",
  },
  topScorers2015: {
    name: "TopScorers.Club",
    url: "https://www.topscorers.club/serie-a/2015-2016/",
    retrievedAt: "2026-06-06",
    fields: ["Serie A goals", "Serie A assists", "Serie A appearances"],
    note: "2015-16 Serie A scorer and assist leaderboard cross-check.",
  },
  worldFootballInter2009: {
    name: "worldfootball.net",
    url: "https://www.worldfootball.net/team_performance/inter/ita-serie-a-2009-2010/",
    retrievedAt: "2026-06-06",
    fields: ["Serie A minutes", "Serie A appearances", "Serie A starts", "substitutions", "goals", "cards"],
    note: "Inter 2009-10 Serie A appearances and goals table.",
  },
  interOfficial2009: {
    name: "Inter",
    url: "https://www.inter.it/en/news/2020-05-20-inter-squad-2009-10-season",
    retrievedAt: "2026-06-06",
    fields: ["all-competition call-ups", "all-competition appearances", "all-competition goals", "minutes"],
    note: "Official Inter Triplete squad appearance table.",
  },
  worldFootballRoma2000: {
    name: "worldfootball.net",
    url: "https://www.worldfootball.net/team_performance/as-roma/ita-serie-a-2000-2001/",
    retrievedAt: "2026-06-06",
    fields: ["Serie A minutes", "Serie A appearances", "Serie A starts", "substitutions", "goals", "cards"],
    note: "Roma 2000-01 Serie A appearances and goals table.",
  },
  statsCrewMilan2003: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-ACMMI200/y-2003",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "assists", "discipline"],
    note: "Milan 2003-04 Serie A scoring, assist and appearance table.",
  },
  acMilanRoster2003: {
    name: "AC Milan",
    url: "https://www.acmilan.com/en/roster-archive/men-first-team-archive/acmilan-2003-roster",
    retrievedAt: "2026-06-06",
    fields: ["roster archive", "season recap", "Scudetto context"],
    note: "Official Milan 2003-04 roster and season recap.",
  },
  statsCrewSamp1990: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-SAMIA857/y-1990",
    retrievedAt: "2026-06-06",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Sampdoria 1990-91 Serie A scoring and appearance table.",
  },
  manualSampdoria1988: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1988%E2%80%9389_UC_Sampdoria_season",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league table", "Serie A appearances", "Serie A goals", "top scorers"],
    note: "Sampdoria 1988-89 fifth-place Serie A season, Coppa Italia win and player-statistics table.",
  },
  statsCrewParma1998: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-PAR19134/y-1998",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "assists", "discipline"],
    note: "Parma 1998-99 Serie A scoring, assist and appearance table.",
  },
  statsCrewNapoli1986: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-SSCNA690/y-1986",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Napoli 1986-87 Serie A scoring and appearance table.",
  },
  bdfutbolMilan1988: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1988-893015.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Milan 1988-89 Serie A squad appearance table.",
  },
  bdfutbolInter1988: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1988-893008.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Inter 1988-89 Serie A squad appearance table.",
  },
  bdfutbolLecce1988: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1988-893012.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Lecce 1988-89 Serie A squad appearance table.",
  },
  manualLecce1988: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Unione_Sportiva_Lecce_1988-1989",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league result", "season top scorer", "player roles", "season summary"],
    note: "Lecce 1988-89 ninth-place Serie A season and role context.",
  },
  bdfutbolUdinese1983: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1983-843005.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Udinese 1983-84 Serie A squad appearance table.",
  },
  statsCrewUdinese2010: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-UDICA546/y-2010",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "assists", "team record"],
    note: "Udinese 2010-11 Serie A team summary and scoring table.",
  },
  bdfutbolUdinese2010: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t2010-113005.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Udinese 2010-11 Serie A squad appearance table.",
  },
  statsCrewFiorentina1998: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-ACFFI248/y-1998",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "goals for", "goals against", "season finish"],
    note: "Fiorentina 1998-99 Serie A roster and team summary.",
  },
  bdfutbolFiorentina1998: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1998-993014.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Fiorentina 1998-99 Serie A squad appearance table.",
  },
  bdfutbolPalermo2009: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t2009-103030.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Palermo 2009-10 Serie A squad appearance table.",
  },
  bdfutbolBologna1997: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1997-983010.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Bologna 1997-98 Serie A squad appearance table.",
  },
  transfermarktBologna1997: {
    name: "Transfermarkt",
    url: "https://www.transfermarkt.com/fc-bologna/startseite/verein/1025/saison_id/1997",
    retrievedAt: "2026-06-07",
    fields: ["season finish", "top goalscorers", "most assists", "squad context"],
    note: "Bologna 1997-98 season context with scorer and assist leaders.",
  },
  bdfutbolAtalanta2018: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t2018-193017.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Atalanta 2018-19 Serie A squad appearance table.",
  },
  statsCrewAtalanta2018: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-ATATA930/y-2018",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "goals for", "goals against"],
    note: "Atalanta 2018-19 Serie A roster and team summary.",
  },
  bdfutbolCagliari1969: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1969-703031.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Cagliari 1969-70 Serie A squad appearance table.",
  },
  statsCrewCagliari1969: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-CAGCA568/y-1969",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "Serie A games played", "Serie A games started", "goals"],
    note: "Cagliari 1969-70 scoring table and first-place team record.",
  },
  manualCagliari1969Positions: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Unione_Sportiva_Cagliari_1969-1970",
    retrievedAt: "2026-06-12",
    fields: ["formation type", "role descriptions", "player appearances"],
    note: "Formation notes for the Scopigno Cagliari title side, including stopper, libero, mediano, regista, wing and false-nine role details.",
  },
  manualInter1970: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Serie_A_1970-1971",
    retrievedAt: "2026-06-12",
    fields: ["champion team", "player appearances", "top scorer", "league table"],
    note: "Inter 1970-71 champion-team table with typical XI appearances and Boninsegna capocannoniere line.",
  },
  bdfutbolTorino1975: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1975-763019.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Torino 1975-76 Serie A squad appearance table.",
  },
  statsCrewTorino1975: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-TORFC252/y-1975",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "goals for", "goals against", "top scorers"],
    note: "Torino 1975-76 roster and first-place team summary.",
  },
  manualTorino1975Positions: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Serie_A_1975-1976",
    retrievedAt: "2026-06-12",
    fields: ["champion XI", "player appearances", "role groups"],
    note: "Champion-team formation and role-group context for Torino 1975-76.",
  },
  manualLazio1973: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1973%E2%80%9374_SS_Lazio_season",
    retrievedAt: "2026-06-11",
    fields: ["squad", "league result", "manager", "league top scorer", "season summary"],
    note: "Lazio 1973-74 first-scudetto squad context.",
  },
  rsssfSerieA1973: {
    name: "RSSSF",
    url: "https://www.rsssf.org/tablesi/ital74.html",
    retrievedAt: "2026-06-11",
    fields: ["league table", "results", "top scorers"],
    note: "1973-74 Serie A season and top-scorer cross-check.",
  },
  manualJuventus1976: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1976%E2%80%9377_Juventus_FC_season",
    retrievedAt: "2026-06-11",
    fields: ["squad", "league result", "top scorers", "UEFA Cup run", "season summary"],
    note: "Juventus 1976-77 scudetto and UEFA Cup squad context.",
  },
  rsssfSerieA1976: {
    name: "RSSSF",
    url: "https://www.rsssf.org/tablesi/ital77.html",
    retrievedAt: "2026-06-11",
    fields: ["league table", "results", "top scorers"],
    note: "1976-77 Serie A season and top-scorer cross-check.",
  },
  manualMilan1978: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1978%E2%80%9379_AC_Milan_season",
    retrievedAt: "2026-06-11",
    fields: ["squad", "league result", "season top scorer", "competition summary"],
    note: "Milan 1978-79 tenth-scudetto squad context.",
  },
  manualRoma1982: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1982%E2%80%9383_AS_Roma_season",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league result", "Serie A appearances", "Serie A goals", "season top scorer"],
    note: "Roma 1982-83 second-scudetto squad and player-statistics table.",
  },
  manualJuventus1981: {
    name: "Manual cross-check",
    url: "https://en.wikipedia.org/wiki/1981%E2%80%9382_Juventus_FC_season",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league table", "Serie A appearances", "Serie A goals", "season top scorer"],
    note: "Juventus 1981-82 twentieth-scudetto squad and player-statistics table.",
  },
  rsssfSerieA1978: {
    name: "RSSSF",
    url: "https://www.rsssf.org/tablesi/ital79.html",
    retrievedAt: "2026-06-11",
    fields: ["league table", "results", "top scorers"],
    note: "1978-79 Serie A season and top-scorer cross-check.",
  },
  bdfutbolPerugia1978: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1978-793011.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Perugia 1978-79 Serie A squad appearance table.",
  },
  manualPerugia1978: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Associazione_Calcio_Perugia_1978-1979",
    retrievedAt: "2026-06-12",
    fields: ["season summary", "league result", "top scorer", "formation notes", "role notes"],
    note: "Perugia 1978-79 unbeaten runner-up season and tactical role context.",
  },
  bdfutbolNapoli1974: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1974-753003.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Napoli 1974-75 Serie A squad appearance table.",
  },
  manualNapoli1974: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Societ%C3%A0_Sportiva_Calcio_Napoli_1974-1975",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league result", "team goals", "top scorers", "season summary"],
    note: "Napoli 1974-75 second-place season, league-best attack and scorer context.",
  },
  bdfutbolPerugia1998: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1998-993011.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Perugia 1998-99 Serie A squad appearance table.",
  },
  statsCrewPerugia1998: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-ACPCA207/y-1998",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "top scorers", "goals against", "goalkeeper context"],
    note: "Perugia 1998-99 statistics and fourteenth-place team summary.",
  },
  bdfutbolBari1999: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1999-003001.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Bari 1999-00 Serie A squad appearance table.",
  },
  statsCrewBari1999: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-SSCBA687/y-1999",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "top scorers", "squad context"],
    note: "Bari 1999-00 roster and fourteenth-place team summary.",
  },
  bdfutbolVicenza1997: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1997-983016.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Vicenza 1997-98 Serie A squad appearance table.",
  },
  statsCrewVicenza1997: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-VICVI995/y-1997",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "Serie A games played", "Serie A games started", "goals"],
    note: "Vicenza 1997-98 fourteenth-place team summary and scoring table.",
  },
  bdfutbolBrescia2000: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t2000-013006.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Brescia 2000-01 Serie A squad appearance table.",
  },
  statsCrewBrescia2000: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-BRECA427/y-2000",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "top scorers", "goals for", "goals against"],
    note: "Brescia 2000-01 eighth-place team summary with Baggio and Hubner context.",
  },
  bdfutbolPiacenza1997: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1997-983021.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Piacenza 1997-98 Serie A squad appearance table.",
  },
  bdfutbolVenezia1998: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1998-993020.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Venezia 1998-99 Serie A squad appearance table.",
  },
  statsCrewVenezia1998: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-VENFC869/y-1998",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "goals for", "goals against", "top scorers"],
    note: "Venezia 1998-99 eleventh-place team summary and scoring context.",
  },
  bdfutbolReggina1999: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1999-003007.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Reggina 1999-00 Serie A squad appearance table.",
  },
  statsCrewReggina1999: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-REG19634/y-1999",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "top scorers", "goalkeeper context"],
    note: "Reggina 1999-00 twelfth-place debut Serie A team summary.",
  },
  bdfutbolLecce1999: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1999-003012.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Lecce 1999-00 Serie A squad appearance table.",
  },
  statsCrewLecce1999: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-USLLE715/y-1999",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "top scorers", "assists", "goalkeeper context"],
    note: "Lecce 1999-00 thirteenth-place team summary with Lucarelli and Sesa context.",
  },
  bdfutbolSalernitana1998: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1998-993042.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Salernitana 1998-99 Serie A squad appearance table.",
  },
  statsCrewSalernitana1998: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-USS19735/y-1998",
    retrievedAt: "2026-06-07",
    fields: ["roster", "team record", "season finish", "top scorers", "assists", "goalkeeper context"],
    note: "Salernitana 1998-99 fifteenth-place team summary with Di Vaio and Balli context.",
  },
  bdfutbolEmpoli1997: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1997-983025.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Empoli 1997-98 Serie A squad appearance table.",
  },
  footballSquadsEmpoli1997: {
    name: "FootballSquads",
    url: "https://www.footballsquads.co.uk/italy/1997-1998/seriea/empoli.htm",
    retrievedAt: "2026-06-07",
    fields: ["roster", "positions", "manager", "stadium"],
    note: "Empoli 1997-98 roster and position cross-check.",
  },
  bdfutbolFoggia1991: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1991-923046.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Foggia 1991-92 Serie A squad appearance table.",
  },
  bdfutbolFiorentina1981: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1981-823014.html",
    retrievedAt: "2026-06-12",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Fiorentina 1981-82 Serie A squad appearance table.",
  },
  manualFiorentina1981: {
    name: "Manual cross-check",
    url: "https://it.wikipedia.org/wiki/Associazione_Calcio_Fiorentina_1981-1982",
    retrievedAt: "2026-06-12",
    fields: ["squad", "league result", "role groups", "top scorer", "season summary"],
    note: "Fiorentina 1981-82 runner-up season, role-group and title-race context.",
  },
  bdfutbolVerona1984: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t1984-853002.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "Hellas Verona 1984-85 Serie A squad appearance table.",
  },
  statsCrewVerona1984: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/stats/t-HELVE347/y-1984",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "Serie A games played", "Serie A games started", "goals", "goals allowed"],
    note: "Hellas Verona 1984-85 scoring table and first-place team record.",
  },
  bdfutbolChievo2001: {
    name: "BDFutbol",
    url: "https://www.bdfutbol.com/en/t/t2001-023022.html",
    retrievedAt: "2026-06-07",
    fields: ["Serie A appearances", "Serie A starts", "minutes", "goals", "discipline"],
    note: "ChievoVerona 2001-02 Serie A squad appearance table.",
  },
  statsCrewChievo2001: {
    name: "StatsCrew",
    url: "https://www.statscrew.com/worldfootball/roster/t-CHIVE968/y-2001",
    retrievedAt: "2026-06-07",
    fields: ["team record", "season finish", "goals for", "goals against", "top scorers", "assists"],
    note: "ChievoVerona 2001-02 roster and fifth-place team summary.",
  },
} satisfies Record<string, NonNullable<PlayerSeason["evidence"]>[number]>;

const curatedPlayers: PlayerSeason[] = [
  {
    id: "zoff-juv-81",
    name: "Dino Zoff",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 94,
    appearances: 30,
    starts: 30,
    tag: "Mito bianconero",
    note: "Trenta presenze in Serie A e ultima grande corsa scudetto prima del Mondiale 1982.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "maradona-nap-86",
    name: "Diego Maradona",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Argentina",
    score: 99,
    goals: 10,
    appearances: 29,
    starts: 29,
    tag: "Scudetto del Sud",
    note: "Il prompt che cambia subito il peso della simulazione.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "albertosi-cag-69",
    name: "Enrico Albertosi",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 92,
    appearances: 30,
    starts: 30,
    tag: "Porta scudetto",
    note: "Il portiere del Cagliari campione, difesa storica e Sardegna in cima alla Serie A.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "niccolai-cag-69",
    name: "Comunardo Niccolai",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    appearances: 29,
    starts: 29,
    tag: "Marcatura sarda",
    note: "Difensore della linea che rese il Cagliari campione e quasi impenetrabile.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "zignoli-cag-69",
    name: "Giulio Zignoli",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 85,
    appearances: 25,
    starts: 24,
    tag: "Terzino scudetto",
    note: "Fascia, attenzione e partite pesanti nella prima grande favola rossoblu.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "martiradonna-cag-69",
    name: "Mario Martiradonna",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 28,
    tag: "Muro rossoblu",
    note: "Titolare fisso nella difesa che porta lo scudetto sull'isola.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "tomasini-cag-69",
    name: "Giuseppe Tomasini",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 17,
    starts: 17,
    tag: "Rotazione storica",
    note: "Difensore utile per completare l'ossatura del Cagliari campione.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "cera-cag-69",
    name: "Pier Luigi Cera",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "centrale"],
    country: "Italia",
    score: 90,
    appearances: 27,
    starts: 27,
    tag: "Libero regista",
    note: "Intelligenza tattica e raccordo tra difesa e centrocampo nel Cagliari di Scopigno.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "nene-cag-69",
    name: "Nene",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["mezzala", "seconda-punta", "mediano"],
    country: "Brasile",
    score: 89,
    goals: 3,
    appearances: 28,
    starts: 28,
    tag: "Classe paulista",
    note: "Tecnica brasiliana e ritmo da mezzapunta nella favola rossoblu.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "greatti-cag-69",
    name: "Ricciotti Greatti",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 27,
    starts: 27,
    tag: "Cervello operaio",
    note: "Equilibrio, corsa e un gol nel centrocampo campione d'Italia.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "domenghini-cag-69",
    name: "Angelo Domenghini",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 8,
    appearances: 30,
    starts: 30,
    tag: "Ala da Messico",
    note: "Trenta presenze, otto gol e strappi da nazionale nel Cagliari scudetto.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "gori-cag-69",
    name: "Sergio Gori",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 88,
    goals: 6,
    appearances: 30,
    starts: 30,
    tag: "Spalla di Riva",
    note: "Movimento e gol pesanti accanto a Rombo di Tuono.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "riva-cag-69",
    name: "Gigi Riva",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta", "ala"],
    country: "Italia",
    score: 97,
    goals: 21,
    appearances: 28,
    starts: 28,
    tag: "Rombo di Tuono",
    note: "Ventuno gol e uno scudetto impossibile: la carta nostalgia per definizione.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "brugnera-cag-69",
    name: "Mario Brugnera",
    club: "Cagliari",
    season: "1969-70",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["mezzala", "seconda-punta", "mediano"],
    country: "Italia",
    score: 84,
    goals: 3,
    appearances: 19,
    starts: 16,
    tag: "Cambio storico",
    note: "Rotazione vera, tre gol e profondita per completare il blocco scudetto.",
    evidence: [evidenceRefs.bdfutbolCagliari1969, evidenceRefs.statsCrewCagliari1969, evidenceRefs.manualCagliari1969Positions],
  },
  {
    id: "vieri-inter-70",
    name: "Lido Vieri",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 91,
    appearances: 24,
    starts: 24,
    tag: "Porta sorpasso",
    note: "Portiere della rimonta nerazzurra: sicurezza e partite pesanti nel titolo di Invernizzi.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "bordon-inter-70",
    name: "Ivano Bordon",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 9,
    starts: 9,
    tag: "Cambio in porta",
    note: "Giovane portiere gia dentro la rosa scudetto, utile per allargare il blocco Inter.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "bellugi-inter-70",
    name: "Mauro Bellugi",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 87,
    appearances: 19,
    starts: 19,
    tag: "Difesa giovane",
    note: "Terzino e marcatore moderno nella linea che accompagna il sorpasso scudetto.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "facchetti-inter-70",
    name: "Giacinto Facchetti",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 95,
    appearances: 30,
    starts: 30,
    tag: "Capitano verticale",
    note: "Trenta presenze, fascia sinistra e leadership nella seconda Inter campione.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "fabbian-inter-70",
    name: "Giancarlo Fabbian",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 17,
    starts: 17,
    tag: "Rotazione difensiva",
    note: "Presenze vere da stopper per dare profondita alla retroguardia nerazzurra.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "cella-inter-70",
    name: "Giancarlo Cella",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "terzino"],
    country: "Italia",
    score: 82,
    appearances: 6,
    starts: 6,
    tag: "Cambio arretrato",
    note: "Difensore di complemento nella rosa che completa la rimonta scudetto.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "giubertoni-inter-70",
    name: "Gianfranco Giubertoni",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    appearances: 27,
    starts: 27,
    tag: "Libero nerazzurro",
    note: "Letture e continuita nella difesa che sostiene Boninsegna e Mazzola.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "burgnich-inter-70",
    name: "Tarcisio Burgnich",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto", "terzino"],
    country: "Italia",
    score: 93,
    appearances: 29,
    starts: 29,
    tag: "Roccia d'Inter",
    note: "Marcatura, esperienza e quasi sempre in campo nello scudetto del 1971.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "bedin-inter-70",
    name: "Gianfranco Bedin",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 88,
    appearances: 23,
    starts: 23,
    tag: "Polmone nerazzurro",
    note: "Equilibrio, coperture e corse nel centrocampo che regge il cambio di passo.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "bertini-inter-70",
    name: "Mario Bertini",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 88,
    appearances: 26,
    starts: 26,
    tag: "Mediana mondiale",
    note: "Centrocampista di corsa e nazionale, ventisei presenze nel titolo nerazzurro.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "oriali-inter-70",
    name: "Gabriele Oriali",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 82,
    appearances: 2,
    starts: 2,
    tag: "Esordio operaio",
    note: "Pochi minuti ma gia una traccia del futuro equilibratore nerazzurro.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "jair-inter-70",
    name: "Jair da Costa",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Brasile",
    score: 89,
    appearances: 23,
    starts: 23,
    tag: "Strappo brasiliano",
    note: "Ampiezza, accelerazioni e memoria della Grande Inter nel nuovo scudetto.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "corso-inter-70",
    name: "Mario Corso",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "trequartista", "esterno"],
    country: "Italia",
    score: 92,
    appearances: 29,
    starts: 29,
    tag: "Sinistro di Dio",
    note: "Classe laterale, punizioni e ultima luce tecnica della vecchia Inter.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "mazzola-inter-70",
    name: "Sandro Mazzola",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 94,
    appearances: 29,
    starts: 29,
    tag: "Dieci del sorpasso",
    note: "Rifinitura, leadership e intelligenza offensiva nel titolo dopo la rimonta.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "boninsegna-inter-70",
    name: "Roberto Boninsegna",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti"],
    country: "Italia",
    score: 97,
    goals: 24,
    appearances: 28,
    starts: 28,
    tag: "Bonimba capocannoniere",
    note: "Ventiquattro gol, titolo cannonieri e peso totale nell'Inter dello scudetto.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "frustalupi-inter-70",
    name: "Mario Frustalupi",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 88,
    appearances: 18,
    starts: 18,
    tag: "Regia prima della Lazio",
    note: "Diciotto presenze e piedi puliti prima di diventare cervello biancoceleste.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "pellizzaro-inter-70",
    name: "Roberto Pellizzaro",
    club: "Inter",
    season: "1970-71",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 82,
    appearances: 4,
    starts: 4,
    tag: "Cambio d'attacco",
    note: "Profondita offensiva per completare la rosa campione senza ripetere i soliti nomi.",
    evidence: [evidenceRefs.manualInter1970],
  },
  {
    id: "castellini-tor-75",
    name: "Luciano Castellini",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 92,
    appearances: 29,
    starts: 29,
    tag: "Giaguaro granata",
    note: "Il portiere del Toro scudetto: riflessi, piazzamento e porta quasi blindata.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "mozzini-tor-75",
    name: "Roberto Mozzini",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 88,
    appearances: 29,
    starts: 29,
    tag: "Muro Radice",
    note: "Difensore centrale della linea meno battuta del campionato 1975-76.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "salvadori-tor-75",
    name: "Roberto Salvadori",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 87,
    appearances: 30,
    starts: 30,
    tag: "Fascia granata",
    note: "Trenta partite e duttilita per tenere compatta la squadra di Radice.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "caporale-tor-75",
    name: "Vittorio Caporale",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 28,
    tag: "Marcatura antica",
    note: "Difensore solido nel Toro che vince partite sporche e derby pesantissimi.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "santin-tor-75",
    name: "Nello Santin",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 25,
    starts: 25,
    tag: "Linea scudetto",
    note: "Esperienza, letture e coperture nella difesa che concede pochissimo.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "psala-tor-75",
    name: "Patrizio Sala",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "mezzala", "terzino"],
    country: "Italia",
    score: 88,
    appearances: 30,
    starts: 30,
    tag: "Motorino granata",
    note: "Corsa continua e disciplina tattica: il pressing di Radice passa anche da lui.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "csala-tor-75",
    name: "Claudio Sala",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 92,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Poeta del gol",
    note: "Cross, dribbling e fantasia: il servizio perfetto per Pulici e Graziani.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "pecci-tor-75",
    name: "Eraldo Pecci",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Regia granata",
    note: "Vent'anni, personalita e tempi di gioco nel centrocampo dello scudetto.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "zaccarelli-tor-75",
    name: "Renato Zaccarelli",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 89,
    goals: 4,
    appearances: 28,
    starts: 28,
    tag: "Inserimento Toro",
    note: "Quattro gol e continuita da mezzala in una squadra che correva tutta assieme.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "pulici-tor-75",
    name: "Paolino Pulici",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 97,
    goals: 21,
    appearances: 30,
    starts: 30,
    tag: "Pupi capocannoniere",
    note: "Ventuno gol e scudetto granata: una carta da aprire con la sigla di 90 Minuto.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "graziani-tor-75",
    name: "Francesco Graziani",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 94,
    goals: 15,
    appearances: 29,
    starts: 29,
    tag: "Ciccio gemello",
    note: "Quindici gol, fisico e intesa totale con Pulici nella coppia scudetto.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "gorin-tor-75",
    name: "Fabrizio Gorin",
    club: "Torino",
    season: "1975-76",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "mediano"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 12,
    starts: 9,
    tag: "Rotazione Radice",
    note: "Minuti veri e un gol per dare profondita al blocco granata.",
    evidence: [evidenceRefs.bdfutbolTorino1975, evidenceRefs.statsCrewTorino1975, evidenceRefs.manualTorino1975Positions],
  },
  {
    id: "pulici-laz-73",
    name: "Felice Pulici",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 90,
    tag: "Porta Maestrelli",
    note: "Il portiere della Lazio del primo scudetto, presenza sobria in una squadra elettrica.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "wilson-laz-73",
    name: "Giuseppe Wilson",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale"],
    country: "Italia",
    score: 92,
    tag: "Capitano scudetto",
    note: "Leadership, marcatura e nervi da capitano nella Lazio campione.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "petrelli-laz-73",
    name: "Sergio Petrelli",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 86,
    tag: "Fascia biancoceleste",
    note: "Terzino ruvido e affidabile per dare profondita al blocco anni Settanta.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "oddi-laz-73",
    name: "Giancarlo Oddi",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    tag: "Marcatura Olimpia",
    note: "Centrale da duelli, perfetto per una linea difensiva di carattere.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "martini-laz-73",
    name: "Luigi Martini",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 87,
    tag: "Pendolino Lazio",
    note: "Fascia, corsa e coperture nella squadra di Maestrelli.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "frustalupi-laz-73",
    name: "Mario Frustalupi",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 91,
    tag: "Regia scudetto",
    note: "Il cervello basso della Lazio campione: tempi, pausa e palloni puliti.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "rececconi-laz-73",
    name: "Luciano Re Cecconi",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 91,
    tag: "Angelo biondo",
    note: "Corsa, inserimenti e identita totale nel centrocampo dello scudetto.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "nanni-laz-73",
    name: "Franco Nanni",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    tag: "Equilibrio Maestrelli",
    note: "Centrocampista di sostanza per tenere unita una squadra piena di tensione.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "damico-laz-73",
    name: "Vincenzo D'Amico",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "ala", "seconda-punta"],
    country: "Italia",
    score: 89,
    tag: "Ragazzo del Flaminio",
    note: "Fantasia giovane e strappi creativi nel lato piu tecnico della Lazio.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "garlaschelli-laz-73",
    name: "Renzo Garlaschelli",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "ala"],
    country: "Italia",
    score: 90,
    goals: 10,
    tag: "Spalla da dieci gol",
    note: "Dieci gol e movimento continuo accanto a Chinaglia.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "chinaglia-laz-73",
    name: "Giorgio Chinaglia",
    club: "Lazio",
    season: "1973-74",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti"],
    country: "Italia",
    score: 97,
    goals: 24,
    tag: "Long John",
    note: "Capocannoniere e trascinatore della Lazio del primo scudetto.",
    evidence: [evidenceRefs.manualLazio1973, evidenceRefs.rsssfSerieA1973],
  },
  {
    id: "zoff-juv-76",
    name: "Dino Zoff",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 94,
    tag: "Porta Trapattoni",
    note: "Sicurezza assoluta nella Juventus che vince scudetto e Coppa UEFA.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "gentile-juv-76",
    name: "Claudio Gentile",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 91,
    tag: "Marcatura feroce",
    note: "Duelli, cattiveria agonistica e disciplina nella prima Juve di Trapattoni.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "scirea-juv-76",
    name: "Gaetano Scirea",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 94,
    tag: "Libero elegante",
    note: "Uscita pulita e letture da campione in una Juve durissima.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "cabrini-juv-76",
    name: "Antonio Cabrini",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 89,
    goals: 1,
    tag: "Bell'Antonio",
    note: "Giovane fascia sinistra, gia dentro una stagione da doppio trionfo.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "cuccureddu-juv-76",
    name: "Antonello Cuccureddu",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "mezzala", "mediano"],
    country: "Italia",
    score: 90,
    tag: "Jolly bianconero",
    note: "Carta elastica, fisica e preziosa per incastrare difesa e centrocampo.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "furino-juv-76",
    name: "Giuseppe Furino",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 88,
    tag: "Ringhio antico",
    note: "Recuperi, contrasti e leadership nel cuore della Juve operaia.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "tardelli-juv-76",
    name: "Marco Tardelli",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 92,
    tag: "Strappo mondiale",
    note: "Inserimenti e intensita da centrocampista totale nella Juve di Trap.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "benetti-juv-76",
    name: "Romeo Benetti",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 89,
    tag: "Durezza bianconera",
    note: "Fisico e tiro pesante per una mediana che non arretra mai.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "causio-juv-76",
    name: "Franco Causio",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 92,
    tag: "Barone sulla fascia",
    note: "Dribbling, cross e classe laterale per accendere Bettega e Boninsegna.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "bettega-juv-76",
    name: "Roberto Bettega",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 95,
    goals: 17,
    tag: "Cabeza blanca",
    note: "Diciassette gol in campionato e classe da centravanti moderno.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "boninsegna-juv-76",
    name: "Roberto Boninsegna",
    club: "Juventus",
    season: "1976-77",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 10,
    tag: "Bonimba bianconero",
    note: "Veterano da area, dieci gol e peso specifico nella corsa scudetto.",
    evidence: [evidenceRefs.manualJuventus1976, evidenceRefs.rsssfSerieA1976],
  },
  {
    id: "albertosi-mil-78",
    name: "Enrico Albertosi",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 92,
    tag: "Porta della stella",
    note: "Esperienza e nervi nel Milan della decima stella.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "baresi-mil-78",
    name: "Franco Baresi",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 91,
    tag: "Libero diciottenne",
    note: "Gia titolare nel Milan campione, anticipo e personalita da predestinato.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "collovati-mil-78",
    name: "Fulvio Collovati",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale"],
    country: "Italia",
    score: 90,
    tag: "Centrale da stella",
    note: "Marcatura e compostezza nella linea che protegge la decima.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "amaldera-mil-78",
    name: "Aldo Maldera",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 91,
    goals: 9,
    tag: "Terzino goleador",
    note: "Nove gol e spinta continua: un terzino che cambia il draft.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "bet-mil-78",
    name: "Aldo Bet",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale"],
    country: "Italia",
    score: 86,
    tag: "Esperienza rossonera",
    note: "Difensore di rotazione solida per allargare la memoria della stella.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "devecchi-mil-78",
    name: "Walter De Vecchi",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 87,
    tag: "Nuovo innesto",
    note: "Corsa e inserimenti nel centrocampo del Milan di Liedholm.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "buriani-mil-78",
    name: "Ruben Buriani",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 88,
    tag: "Polmone rossonero",
    note: "Equilibrio e lavoro sporco accanto ai piedi piu nobili della rosa.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "novellino-mil-78",
    name: "Walter Novellino",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["esterno", "ala", "mezzala"],
    country: "Italia",
    score: 88,
    tag: "Fascia di Liedholm",
    note: "Esterno di raccordo per dare ampiezza a una squadra paziente.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "rivera-mil-78",
    name: "Gianni Rivera",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "mezzala"],
    country: "Italia",
    score: 94,
    tag: "Ultimo Abatino",
    note: "Ultima stagione, microfono a San Siro e decima stella prima dell'addio.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "bigon-mil-78",
    name: "Alberto Bigon",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 12,
    tag: "Bomber della stella",
    note: "Dodici gol in campionato e peso offensivo nella corsa al titolo.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "chiodi-mil-78",
    name: "Stefano Chiodi",
    club: "Milan",
    season: "1978-79",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 86,
    tag: "Rotazione d'area",
    note: "Punta di supporto per completare il blocco del Milan campione.",
    evidence: [evidenceRefs.manualMilan1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "malizia-per-78",
    name: "Nello Malizia",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 88,
    appearances: 28,
    starts: 27,
    tag: "Porta imbattuta",
    note: "Il portiere del Perugia dei miracoli: ventotto presenze e appena quindici reti subite nella tabella BDFutbol.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "nappi-per-78",
    name: "Michele Nappi",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 84,
    appearances: 21,
    starts: 21,
    tag: "Fascia grifone",
    note: "Laterale difensivo con ventuno partenze nella squadra che non perde mai.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "ceccarini-per-78",
    name: "Antonio Ceccarini",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Presenza lunga",
    note: "Ventinove presenze, un gol e continuita totale sul lato della linea di Castagner.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "dellamartira-per-78",
    name: "Mauro Della Martira",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    appearances: 29,
    starts: 29,
    tag: "Stopper viola",
    note: "Innesto da stopper, ventinove partenze e marcatura solida nel Perugia imbattuto.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "frosio-per-78",
    name: "Pierluigi Frosio",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 89,
    goals: 1,
    appearances: 24,
    starts: 24,
    tag: "Libero di Castagner",
    note: "Capitano e libero, autorizzato ad accompagnare il gioco fino al centrocampo.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "dalfiume-per-78",
    name: "Paolo Dal Fiume",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    goals: 3,
    appearances: 28,
    starts: 28,
    tag: "Equilibrio umbro",
    note: "Ventotto partenze e tre gol nella mediana che regge il 1-3-2-3-1.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "bagni-per-78",
    name: "Salvatore Bagni",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 8,
    appearances: 28,
    starts: 28,
    tag: "Ala rivelazione",
    note: "Ala offensiva e mezzala di rottura: otto gol nella stagione dell'esplosione ad alto livello.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "butti-per-78",
    name: "Cesare Butti",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 26,
    starts: 26,
    tag: "Motore biancorosso",
    note: "Il motore del centrocampo indicato nel racconto tattico della stagione.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "redeghieri-per-78",
    name: "Giorgio Redeghieri",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 82,
    appearances: 23,
    starts: 20,
    tag: "Seconda linea utile",
    note: "Giovane di rotazione con ventitre presenze e minuti veri nella corsa europea.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "vannini-per-78",
    name: "Franco Vannini",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "regista"],
    country: "Italia",
    score: 88,
    goals: 3,
    appearances: 17,
    starts: 17,
    tag: "Mezzala chiave",
    note: "Il racconto tattico lo mette al centro della manovra: qualita tecnica e inserimenti prima dell'infortunio.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "casarsa-per-78",
    name: "Gianfranco Casarsa",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti", "trequartista"],
    country: "Italia",
    score: 87,
    goals: 4,
    appearances: 28,
    starts: 28,
    tag: "Centravanti arretrato",
    note: "Rigorista e punta arretrata alla Hidegkuti, usata per liberare gli inserimenti da dietro.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978],
  },
  {
    id: "speggiorin-per-78",
    name: "Walter Speggiorin",
    club: "Perugia",
    season: "1978-79",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["ala", "seconda-punta", "esterno"],
    country: "Italia",
    score: 90,
    goals: 9,
    appearances: 26,
    starts: 26,
    tag: "Bomber dei miracoli",
    note: "Miglior marcatore in campionato del Perugia imbattuto e nome da top scorer 1978-79.",
    evidence: [evidenceRefs.bdfutbolPerugia1978, evidenceRefs.manualPerugia1978, evidenceRefs.rsssfSerieA1978],
  },
  {
    id: "carmignani-nap-74",
    name: "Pietro Carmignani",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 88,
    appearances: 28,
    starts: 28,
    tag: "Porta Vinicio",
    note: "Portiere titolare del Napoli secondo, dentro una squadra da cinquanta gol.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "burgnich-nap-74",
    name: "Tarcisio Burgnich",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 91,
    appearances: 30,
    starts: 30,
    tag: "Roccia azzurra",
    note: "Trenta presenze da leader difensivo nel Napoli che arriva a due punti dallo scudetto.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "lapalma-nap-74",
    name: "Antonio La Palma",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 27,
    starts: 27,
    tag: "Fascia nuova",
    note: "Ventisette presenze e due gol nel lato mobile del Napoli di Vinicio.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "bruscolotti-nap-74",
    name: "Giuseppe Bruscolotti",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 89,
    goals: 2,
    appearances: 27,
    starts: 27,
    tag: "Capitano in costruzione",
    note: "Ventisette presenze e durezza partenopea prima degli anni da bandiera assoluta.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "pogliana-nap-74",
    name: "Luigi Pogliana",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 15,
    starts: 14,
    tag: "Rotazione difensiva",
    note: "Quattordici partenze per tenere profonda la linea nella doppia corsa campionato-Europa.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "juliano-nap-74",
    name: "Antonio Juliano",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Italia",
    score: 92,
    goals: 3,
    appearances: 29,
    starts: 29,
    tag: "Core 'ngrato",
    note: "Regia e appartenenza nel centrocampo del Napoli del calcio totale.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "esposito-nap-74",
    name: "Salvatore Esposito",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Corsa azzurra",
    note: "Ventotto partenze e lavoro continuo per sostenere il reparto piu prolifico del torneo.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "orlandini-nap-74",
    name: "Andrea Orlandini",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "mezzala"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 28,
    starts: 28,
    tag: "Ampiezza Vinicio",
    note: "Titolare largo per dare campo e rotazioni all'attacco da cinquanta reti.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "rampanti-nap-74",
    name: "Rosario Rampanti",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["CEN"],
    positionTags: ["mezzala", "trequartista"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 23,
    starts: 22,
    tag: "Raccordo offensivo",
    note: "Centrocampista offensivo di rotazione pesante nella squadra che spinge sempre.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "braglia-nap-74",
    name: "Giorgio Braglia",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "seconda-punta", "esterno"],
    country: "Italia",
    score: 91,
    goals: 12,
    appearances: 29,
    starts: 28,
    tag: "Dodici gol larghi",
    note: "Esterno offensivo e finalizzatore: dodici reti nella miglior macchina da gol del campionato.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "clerici-nap-74",
    name: "Sergio Clerici",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Brasile",
    score: 92,
    goals: 14,
    appearances: 29,
    starts: 29,
    tag: "Bomber del quasi scudetto",
    note: "Quattordici gol in Serie A, riferimento del Napoli secondo a due punti.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "massa-nap-74",
    name: "Giuseppe Massa",
    club: "Napoli",
    season: "1974-75",
    era: "anni70",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "trequartista", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 9,
    appearances: 26,
    starts: 24,
    tag: "Nove gol di fantasia",
    note: "Attaccante di raccordo e terza fonte di gol nel festival offensivo azzurro.",
    evidence: [evidenceRefs.bdfutbolNapoli1974, evidenceRefs.manualNapoli1974],
  },
  {
    id: "galli-fio-81",
    name: "Giovanni Galli",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 90,
    appearances: 30,
    starts: 30,
    tag: "Porta viola",
    note: "Sempre presente nella Fiorentina che sfiora lo scudetto fino all'ultima domenica.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "galbiati-fio-81",
    name: "Roberto Galbiati",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    appearances: 29,
    starts: 29,
    tag: "Linea viola",
    note: "Difensore da ventinove presenze nella seconda miglior difesa del campionato.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "contratto-fio-81",
    name: "Renzo Contratto",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 28,
    tag: "Fascia alabardata",
    note: "Terzino di continuita nella stagione del nuovo giglio e della corsa al titolo.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "vierchowod-fio-81",
    name: "Pietro Vierchowod",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 91,
    goals: 2,
    appearances: 28,
    starts: 28,
    tag: "Stopper mondiale",
    note: "Giovane stopper arrivato per alzare il muro viola prima del Mondiale.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "ferroni-fio-81",
    name: "Armando Ferroni",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 84,
    appearances: 27,
    starts: 19,
    tag: "Laterale utile",
    note: "Diciannove partenze vere per dare profondita alla linea di De Sisti.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "pecci-fio-81",
    name: "Eraldo Pecci",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 2,
    appearances: 25,
    starts: 25,
    tag: "Regia Pontello",
    note: "Rinforzo in mediana, tempi e personalita nel cuore della rincorsa viola.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "casagrande-fio-81",
    name: "Francesco Casagrande",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 26,
    starts: 24,
    tag: "Mezzala viola",
    note: "Ventiquattro partenze e tre gol da reparto nella Fiorentina campione d'inverno.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "miani-fio-81",
    name: "Luciano Miani",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    goals: 4,
    appearances: 22,
    starts: 22,
    tag: "Inserimento viola",
    note: "Quattro gol e ventidue presenze per sostenere il centrocampo senza Antognoni.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "bertoni-fio-81",
    name: "Daniel Bertoni",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "trequartista", "seconda-punta"],
    country: "Argentina",
    score: 92,
    goals: 9,
    appearances: 30,
    starts: 30,
    tag: "Buckle viola",
    note: "Trenta presenze, nove gol e strappi decisivi nella corsa punto a punto.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "massaro-fio-81",
    name: "Daniele Massaro",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["esterno", "mezzala", "ala"],
    country: "Italia",
    score: 87,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Massaro prima del Milan",
    note: "Nuovo innesto di gamba, ventinove presenze e lavoro largo nella squadra di De Sisti.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "graziani-fio-81",
    name: "Francesco Graziani",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 92,
    goals: 9,
    appearances: 29,
    starts: 29,
    tag: "Ciccio viola",
    note: "Nove gol in campionato, incluso il gol poi annullato nel finale di Cagliari.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "sacchetti-fio-81",
    name: "Luigi Sacchetti",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "mezzala", "terzino"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 22,
    starts: 9,
    tag: "Cambio di sostanza",
    note: "Rotazione da ventidue presenze per coprire mediana e fascia nella lunga volata.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "antognoni-fio-81",
    name: "Giancarlo Antognoni",
    club: "Fiorentina",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Italia",
    score: 93,
    goals: 3,
    appearances: 16,
    starts: 16,
    tag: "Capitano ferito",
    note: "Sedici presenze e tre gol prima del grave infortunio che cambia la corsa viola.",
    evidence: [evidenceRefs.bdfutbolFiorentina1981, evidenceRefs.manualFiorentina1981],
  },
  {
    id: "garella-ver-84",
    name: "Claudio Garella",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 90,
    appearances: 30,
    starts: 30,
    tag: "Porta del miracolo",
    note: "Il portiere del Verona campione: mani, piedi, istinto e una favola irripetibile.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "tricella-ver-84",
    name: "Roberto Tricella",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 89,
    appearances: 30,
    starts: 30,
    tag: "Capitano Bagnoli",
    note: "Il libero della squadra di Bagnoli, guida della difesa meno battuta.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "lmarangon-ver-84",
    name: "Luciano Marangon",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Fascia scudetto",
    note: "Corsa laterale e due gol nel Verona che concede pochissimo.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "fontolan-ver-84",
    name: "Silvano Fontolan",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 28,
    starts: 28,
    tag: "Linea pulita",
    note: "Difensore stabile nella struttura che permette al Verona di vincere corto.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "briegel-ver-84",
    name: "Hans-Peter Briegel",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["mezzala", "mediano", "terzino"],
    country: "Germania",
    score: 92,
    goals: 9,
    appearances: 27,
    starts: 27,
    tag: "Panzer del Bentegodi",
    note: "Potenza tedesca, inserimenti e nove gol: il jolly che rompe il campionato.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "volpati-ver-84",
    name: "Domenico Volpati",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    appearances: 30,
    starts: 29,
    tag: "Equilibrio gialloblu",
    note: "Mediano continuo e ordinato per dare respiro alla favola di Bagnoli.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "digennaro-ver-84",
    name: "Antonio Di Gennaro",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Italia",
    score: 91,
    goals: 4,
    appearances: 29,
    starts: 29,
    tag: "Regia del miracolo",
    note: "Il cervello tecnico del Verona scudetto, tra ritmo, lanci e ultimo passaggio.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "fanna-ver-84",
    name: "Pietro Fanna",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 87,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Ala da scudetto",
    note: "Ampiezza, strappi e lavoro senza palla nella squadra outsider per eccellenza.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "bruni-ver-84",
    name: "Luciano Bruni",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 27,
    starts: 15,
    tag: "Rotazione operaia",
    note: "Minuti, coperture e un gol per tenere in piedi il centrocampo del Verona.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "galderisi-ver-84",
    name: "Giuseppe Galderisi",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 92,
    goals: 11,
    appearances: 29,
    starts: 29,
    tag: "Nanu scudetto",
    note: "Undici gol, movimento continuo e finalizzazione nella coppia che accende il Bentegodi.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "elkjaer-ver-84",
    name: "Preben Elkjaer",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Danimarca",
    score: 94,
    goals: 8,
    appearances: 23,
    starts: 22,
    tag: "Scarpa persa",
    note: "Otto gol, potenza nordica e l'immagine cult del tiro senza scarpa contro la Juventus.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "sacchetti-ver-84",
    name: "Luigi Sacchetti",
    club: "Verona",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "mezzala", "centrale"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 15,
    starts: 11,
    tag: "Cambio Bagnoli",
    note: "Profondita utile per chiudere l'ossatura gialloblu senza rompere la chimica.",
    evidence: [evidenceRefs.bdfutbolVerona1984, evidenceRefs.statsCrewVerona1984],
  },
  {
    id: "brini-udi-83",
    name: "Ivano Brini",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 86,
    appearances: 26,
    starts: 26,
    tag: "Porta friulana",
    note: "Titolare tra i pali nella prima Udinese di Zico, con ventisei partenze in Serie A.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "edinho-udi-83",
    name: "Edinho",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Brasile",
    score: 91,
    goals: 2,
    appearances: 28,
    starts: 28,
    tag: "Libero brasiliano",
    note: "Eleganza da libero e piedi puliti dietro al laboratorio friulano di Zico.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "galparoli-udi-83",
    name: "Dino Galparoli",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Linea friulana",
    note: "Trenta presenze nella difesa che sostiene il sogno tecnico dell'Udinese anni 80.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "cattaneo-udi-83",
    name: "Cesare Cattaneo",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 83,
    appearances: 23,
    starts: 20,
    tag: "Jolly difensivo",
    note: "Presenze solide da difensore laterale/centrale nella rosa friulana.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "deagostini-udi-83",
    name: "Luigi De Agostini",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 87,
    goals: 1,
    appearances: 27,
    starts: 27,
    tag: "Sinistra lunga",
    note: "Fascia mancina, spinta e ventisette partenze prima del salto da nazionale.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "gerolin-udi-83",
    name: "Manuel Gerolin",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 28,
    starts: 26,
    tag: "Gamba friulana",
    note: "Centrocampo di corsa per coprire campo e lasciare liberta ai brasiliani.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "mauro-udi-83",
    name: "Massimo Mauro",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 27,
    starts: 26,
    tag: "Mezzala giovane",
    note: "Energia e tecnica in una mediana che deve correre anche per Zico.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "causio-udi-83",
    name: "Franco Causio",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 88,
    goals: 2,
    appearances: 28,
    starts: 27,
    tag: "Barone largo",
    note: "Tecnica, cross e mestiere sulla fascia nella versione friulana del Barone.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "virdis-udi-83",
    name: "Pietro Paolo Virdis",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 90,
    goals: 10,
    appearances: 28,
    starts: 28,
    tag: "Spalla da dieci gol",
    note: "Dieci gol in Serie A accanto a Zico: una carta perfetta per rompere i soliti blocchi.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "zico-udi-83",
    name: "Zico",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Brasile",
    score: 97,
    goals: 19,
    appearances: 24,
    starts: 24,
    tag: "Galinho in Friuli",
    note: "Diciannove gol e tecnica irreale: l'Udinese diventa una scelta Nostalgia vera.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "marchetti-udi-83",
    name: "Alberto Marchetti",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 23,
    starts: 20,
    tag: "Cervello di servizio",
    note: "Rotazione tecnica di centrocampo per collegare recupero palla e fantasia.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "pancheri-udi-83",
    name: "Roberto Pancheri",
    club: "Udinese",
    season: "1983-84",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 82,
    appearances: 23,
    starts: 20,
    tag: "Profondita dietro",
    note: "Difensore di rotazione con minuti veri nella rosa di Ferrari.",
    evidence: [evidenceRefs.bdfutbolUdinese1983],
  },
  {
    id: "lupatelli-chi-01",
    name: "Cristiano Lupatelli",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 87,
    appearances: 33,
    starts: 33,
    tag: "Numero dieci in porta",
    note: "Portiere col 10 sulle spalle nel Chievo che fa volare i mussi.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "moro-chi-01",
    name: "Fabio Moro",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Fascia clivense",
    note: "Terzino concreto e sempre presente nella favola di Delneri.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "lanna-chi-01",
    name: "Salvatore Lanna",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 85,
    appearances: 33,
    starts: 31,
    tag: "Sinistra dei mussi",
    note: "Fascia sinistra, minuti e continuita nel Chievo neopromosso da Champions sfiorata.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "dangelo-chi-01",
    name: "Maurizio D'Angelo",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 28,
    tag: "Capitano di quartiere",
    note: "Il capitano che porta una frazione di Verona nella parte alta della Serie A.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "danna-chi-01",
    name: "Lorenzo D'Anna",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 29,
    starts: 28,
    tag: "Linea gialloblu",
    note: "Difensore titolare nella squadra che nessuno voleva affrontare.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "perrotta-chi-01",
    name: "Simone Perrotta",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 89,
    goals: 4,
    appearances: 32,
    starts: 31,
    tag: "Corsa mondiale",
    note: "Mezzala totale prima della Roma e dell'Italia campione del mondo.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "corini-chi-01",
    name: "Eugenio Corini",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 90,
    goals: 9,
    assists: 14,
    appearances: 30,
    starts: 30,
    tag: "Regista dei mussi",
    note: "Nove gol e quattordici assist: la lavagna di Delneri passa dai suoi piedi.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "luciano-chi-01",
    name: "Luciano",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Brasile",
    score: 88,
    goals: 4,
    appearances: 30,
    starts: 30,
    tag: "Eriberto volante",
    note: "Strappi, ampiezza e il vecchio nome sulle fasce del Chievo dei sogni.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "manfredini-chi-01",
    name: "Christian Manfredini",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Costa d'Avorio",
    score: 87,
    goals: 2,
    appearances: 28,
    starts: 27,
    tag: "Fascia elettrica",
    note: "Velocita e pressione sulla corsia opposta nel 4-4-2 che sorprende tutti.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "corradi-chi-01",
    name: "Bernardo Corradi",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 10,
    appearances: 32,
    starts: 31,
    tag: "Torre clivense",
    note: "Centravanti fisico e appoggio continuo per il Chievo che spaventa San Siro.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "marazzina-chi-01",
    name: "Massimo Marazzina",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 90,
    goals: 13,
    appearances: 31,
    starts: 30,
    tag: "Bomber dei mussi",
    note: "Tredici gol nel debutto in Serie A: il simbolo offensivo del miracolo Chievo.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "cossato-chi-01",
    name: "Federico Cossato",
    club: "Chievo",
    season: "2001-02",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 9,
    appearances: 32,
    starts: 7,
    tag: "Panchina pesante",
    note: "Nove gol entrando spesso dalla panchina: profondita vera da provincia europea.",
    evidence: [evidenceRefs.bdfutbolChievo2001, evidenceRefs.statsCrewChievo2001],
  },
  {
    id: "mazzantini-per-98",
    name: "Andrea Mazzantini",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 17,
    starts: 17,
    tag: "Porta dei Grifoni",
    note: "Mezza stagione da titolare nel Perugia di Nakata, tra salvezza e domeniche ruvide.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "matrecano-per-98",
    name: "Salvatore Matrecano",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Rudezza umbra",
    note: "Difensore da duelli veri, con ventinove presenze e un gol nella corsa salvezza.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "colonnello-per-98",
    name: "Gianluca Colonnello",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 84,
    appearances: 29,
    starts: 27,
    tag: "Corsia sinistra",
    note: "Fascia, minuti e applicazione nella squadra che vive di equilibrio e strappi.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "rivas-per-98",
    name: "Martin Rivas",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Argentina",
    score: 85,
    appearances: 26,
    starts: 25,
    tag: "Centrale argentino",
    note: "Difesa sudamericana e marcatura vecchia scuola nel Perugia dei Grifoni.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "ripa-per-98",
    name: "Roberto Ripa",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 82,
    appearances: 21,
    starts: 15,
    tag: "Rotazione difensiva",
    note: "Carta di profondita per completare la retroguardia del Perugia 98-99.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "rapaic-per-98",
    name: "Milan Rapaic",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Croazia",
    score: 89,
    goals: 9,
    appearances: 34,
    starts: 34,
    tag: "Sinistro balcanico",
    note: "Trentaquattro presenze, nove gol e quella sensazione da esterno che puo decidere da solo.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "nakata-per-98",
    name: "Hidetoshi Nakata",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Giappone",
    score: 92,
    goals: 10,
    appearances: 33,
    starts: 33,
    tag: "Samurai a Perugia",
    note: "Dieci gol al primo anno in Serie A: calcio globale prima che diventasse normale.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "olive-per-98",
    name: "Renato Olive",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 27,
    starts: 27,
    tag: "Mediana solida",
    note: "Regolarita, falli tattici e due gol per dare ordine dietro Nakata.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "petrachi-per-98",
    name: "Gianluca Petrachi",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 5,
    appearances: 28,
    starts: 25,
    tag: "Esterno pungente",
    note: "Cinque gol e tanta ampiezza nel Perugia che attacca a fiammate.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "tedesco-per-98",
    name: "Giovanni Tedesco",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    goals: 4,
    appearances: 23,
    starts: 21,
    tag: "Inserimenti umbri",
    note: "Quattro gol da centrocampo e chilometri per tenere vivo il blocco Castagner-Boskov.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "bucchi-per-98",
    name: "Christian Bucchi",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 5,
    appearances: 27,
    starts: 12,
    tag: "Punta di provincia",
    note: "Cinque gol e presenza d'area per completare il tridente narrativo dei Grifoni.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "kaviedes-per-98",
    name: "Ivan Kaviedes",
    club: "Perugia",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Ecuador",
    score: 83,
    goals: 3,
    appearances: 14,
    starts: 12,
    tag: "Colpo esotico",
    note: "Arrivo di mercato, tre gol e profumo di Serie A fine anni 90.",
    evidence: [evidenceRefs.bdfutbolPerugia1998, evidenceRefs.statsCrewPerugia1998],
  },
  {
    id: "mancini-bari-99",
    name: "Francesco Mancini",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 34,
    starts: 34,
    tag: "Porta del San Nicola",
    note: "Titolare fisso nel Bari di Fascetti, trentaquattro presenze e salvezza di provincia.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "delgrosso-bari-99",
    name: "Alessandro Del Grosso",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 83,
    appearances: 30,
    starts: 25,
    tag: "Fascia biancorossa",
    note: "Corsa laterale e partite vere nella stagione in cui Bari resta aggrappata alla A.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "garzya-bari-99",
    name: "Amleto Garzya",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 24,
    starts: 22,
    tag: "Marcatore pugliese",
    note: "Difensore pratico, ruvido e pienamente dentro il calcio di Fascetti.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "innocenti-bari-99",
    name: "Duccio Innocenti",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 4,
    appearances: 28,
    starts: 27,
    tag: "Difensore goleador",
    note: "Quattro gol da dietro: carta perfetta per una Provincia che segna anche coi centrali.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "derosa-bari-99",
    name: "Gaetano De Rosa",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 27,
    starts: 26,
    tag: "Libero del Sud",
    note: "Esperienza e gestione della linea in una squadra costruita per soffrire.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "dandersson-bari-99",
    name: "Daniel Andersson",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Svezia",
    score: 87,
    goals: 5,
    appearances: 32,
    starts: 32,
    tag: "Mediano svedese",
    note: "Trentadue presenze, cinque gol e ordine nordico nel cuore del Bari.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "collauto-bari-99",
    name: "Mattia Collauto",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto"],
    country: "Italia",
    score: 82,
    appearances: 26,
    starts: 26,
    tag: "Esterno operaio",
    note: "Tanta fascia e poche concessioni: il tipo di carta che tiene insieme il modulo.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "marcolini-bari-99",
    name: "Michele Marcolini",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 27,
    starts: 25,
    tag: "Geometria biancorossa",
    note: "Venticinque partenze da titolare e due gol per dare ritmo alla mediana.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "perrotta-bari-99",
    name: "Simone Perrotta",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 31,
    starts: 24,
    tag: "Prima corsa mondiale",
    note: "Prima del Chievo e della Roma, chilometri e verticalita nel Bari di fine millennio.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "osmanovski-bari-99",
    name: "Yksel Osmanovski",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Svezia",
    score: 86,
    goals: 7,
    appearances: 24,
    starts: 22,
    tag: "Seconda punta svedese",
    note: "Sette gol e movimento tra le linee in un Bari pieno di incastri strani.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "spinesi-bari-99",
    name: "Gionatha Spinesi",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 5,
    appearances: 23,
    starts: 14,
    tag: "Punta da salvezza",
    note: "Cinque gol e presenza d'area per allungare una rotazione offensiva molto di provincia.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "ferrari-bari-99",
    name: "Matteo Ferrari",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 26,
    starts: 15,
    tag: "Giovane centrale",
    note: "Prima di diventare nome da Nazionale, minuti e apprendistato nella difesa barese.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "neqrouz-bari-99",
    name: "Rachid Neqrouz",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Marocco",
    score: 84,
    goals: 3,
    appearances: 20,
    starts: 19,
    tag: "Centrale marocchino",
    note: "Tre gol e fisicita in una retroguardia che doveva reggere urti continui.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "cassano-bari-99",
    name: "Antonio Cassano",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 3,
    appearances: 21,
    starts: 14,
    tag: "Bari Vecchia",
    note: "Diciassette anni, tre gol e il colpo contro l'Inter che manda in rewind tutta la Serie A.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "masinga-bari-99",
    name: "Philemon Masinga",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Sudafrica",
    score: 84,
    goals: 1,
    appearances: 11,
    starts: 11,
    tag: "Torre sudafricana",
    note: "Veterano internazionale per dare peso e memoria al reparto offensivo.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "enyinnaya-bari-99",
    name: "Hugo Enyinnaya",
    club: "Bari",
    season: "1999-00",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Nigeria",
    score: 82,
    goals: 2,
    appearances: 12,
    starts: 4,
    tag: "Altro baby Inter",
    note: "Il compagno di notte di Cassano contro l'Inter: carta cult piu che carta rating.",
    evidence: [evidenceRefs.bdfutbolBari1999, evidenceRefs.statsCrewBari1999],
  },
  {
    id: "brivio-vic-97",
    name: "Pierluigi Brivio",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 83,
    appearances: 32,
    starts: 32,
    tag: "Guanti del Menti",
    note: "Titolare quasi fisso nel Vicenza europeo di Guidolin, portiere da provincia vera.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "dicara-vic-97",
    name: "Giacomo Dicara",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 29,
    starts: 29,
    tag: "Difensore col vizio",
    note: "Ventinove presenze, tre gol e marcature da vecchia Serie A.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "coco-vic-97",
    name: "Francesco Coco",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    appearances: 20,
    starts: 17,
    tag: "Fascia in prestito",
    note: "Giovane laterale mancino, abbastanza elastico per cambiare il modulo in corsa.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "belotti-vic-97",
    name: "Davide Belotti",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 27,
    starts: 26,
    tag: "Linea operaia",
    note: "Difensore di continuita per tenere in piedi il blocco biancorosso.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "mendez-vic-97",
    name: "Gustavo Mendez",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Uruguay",
    score: 84,
    goals: 1,
    appearances: 26,
    starts: 22,
    tag: "Uruguay al Menti",
    note: "Braccetto ruvido, ventisei presenze e sangue sudamericano nella provincia veneta.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "dicarlo-vic-97",
    name: "Domenico Di Carlo",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 31,
    starts: 30,
    tag: "Capitano di corsa",
    note: "Trenta partenze da titolare: il metronomo sporco del Vicenza di Guidolin.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "viviani-vic-97",
    name: "Fabio Viviani",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    appearances: 30,
    starts: 29,
    tag: "Mediana Menti",
    note: "Novantanove minuti emotivi condensati in una carta di equilibrio e lavoro.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "schenardi-vic-97",
    name: "Marco Schenardi",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 31,
    starts: 28,
    tag: "Esterno tecnico",
    note: "Fascia, cross e due gol: una lane creativa senza scappare dal tono operaio.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "ambrosini-vic-97",
    name: "Massimo Ambrosini",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 27,
    starts: 25,
    tag: "Prima della Champions",
    note: "Prima delle notti rossonere, un anno di contrasti e inserimenti a Vicenza.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "ambrosetti-vic-97",
    name: "Gabriele Ambrosetti",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 5,
    appearances: 30,
    starts: 21,
    tag: "Strappi biancorossi",
    note: "Cinque gol, accelerazioni e quel tipo di esterno che rende diversa Provincia.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "luiso-vic-97",
    name: "Pasquale Luiso",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 87,
    goals: 8,
    appearances: 29,
    starts: 27,
    tag: "Toro di Sora",
    note: "Otto gol in A, spalle larghe e memoria da Coppa delle Coppe ancora calda.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "zauli-vic-97",
    name: "Lamberto Zauli",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 4,
    appearances: 24,
    starts: 18,
    tag: "Dieci nascosto",
    note: "Quattro gol e fantasia a minuti alterni: la carta che apre le difese chiuse.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "dinapoli-vic-97",
    name: "Arturo Di Napoli",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 6,
    appearances: 24,
    starts: 14,
    tag: "Re Artu in provincia",
    note: "Sei gol dalla rotazione offensiva, perfetto per una scelta non ovvia al draft.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "otero-vic-97",
    name: "Marcelo Otero",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Uruguay",
    score: 84,
    goals: 2,
    appearances: 15,
    starts: 10,
    tag: "Uruguay offensivo",
    note: "Meno continuita, piu memoria: una punta cult per mischiare il blocco Vicenza.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "stovini-vic-97",
    name: "Lorenzo Stovini",
    club: "Vicenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 82,
    appearances: 17,
    starts: 12,
    tag: "Ricambio di marcatura",
    note: "Dodici partenze da titolare per aumentare profondita e ruolo difensivo del blocco.",
    evidence: [evidenceRefs.bdfutbolVicenza1997, evidenceRefs.statsCrewVicenza1997],
  },
  {
    id: "srnicek-bre-00",
    name: "Pavel Srnicek",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Repubblica Ceca",
    score: 84,
    appearances: 26,
    starts: 26,
    tag: "Guanti di Mazzone",
    note: "Titolare nel Brescia ottavo: porta solida per far respirare Baggio e Hubner.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "calori-bre-00",
    name: "Alessandro Calori",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Centrale esperto",
    note: "Trentuno presenze da titolare e leadership ruvida nella linea di Carletto.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "petruzzi-bre-00",
    name: "Fabio Petruzzi",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 28,
    starts: 28,
    tag: "Marcatura pesante",
    note: "Difensore da battaglia, ventotto partenze e un gol in una squadra piena di memoria.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "bonera-bre-00",
    name: "Daniele Bonera",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 26,
    starts: 25,
    tag: "Baby difensore",
    note: "Prima del Milan e della Nazionale, ventisei presenze in una provincia da alta classifica.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "diana-bre-00",
    name: "Aimo Diana",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 32,
    starts: 32,
    tag: "Fascia continua",
    note: "Tuttofare di corsia, trentadue presenze e due gol per dare ampiezza al draft.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "bisoli-bre-00",
    name: "Pier Paolo Bisoli",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 32,
    starts: 24,
    tag: "Mediano di lotta",
    note: "Schermo davanti alla difesa e chilometri per liberare il dieci.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "afilippini-bre-00",
    name: "Antonio Filippini",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 28,
    starts: 23,
    tag: "Gemello operaio",
    note: "Pressing, strappi e provincia bresciana nel cuore della mediana.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "efilippini-bre-00",
    name: "Emanuele Filippini",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    appearances: 28,
    starts: 23,
    tag: "Altro gemello",
    note: "Altra meta del motore Filippini: meno glamour, tanta intesa di spogliatoio.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "pirlo-bre-00",
    name: "Andrea Pirlo",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 88,
    appearances: 10,
    starts: 10,
    tag: "Regista in incubazione",
    note: "In prestito accanto a Baggio: pochi minuti, enorme memoria calcistica.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "baggio-bre-00",
    name: "Roberto Baggio",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 95,
    goals: 10,
    appearances: 25,
    starts: 25,
    tag: "Codino al Rigamonti",
    note: "Dieci gol, Mazzone e la provincia che diventa teatro poetico del calcio italiano.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "hubner-bre-00",
    name: "Dario Hubner",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 17,
    appearances: 31,
    starts: 27,
    tag: "Bisonte",
    note: "Diciassette gol, sigarette immaginate e area di rigore vissuta senza fronzoli.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "tare-bre-00",
    name: "Igli Tare",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Albania",
    score: 83,
    appearances: 13,
    starts: 5,
    tag: "Torre albanese",
    note: "Rotazione lunga, fisico e cross da attaccare quando il draft chiede peso.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "bachini-bre-00",
    name: "Jonathan Bachini",
    club: "Brescia",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 20,
    starts: 20,
    tag: "Strappo di fascia",
    note: "Ventidue metri di corsa e tecnica laterale per collegare Baggio e Hubner.",
    evidence: [evidenceRefs.bdfutbolBrescia2000, evidenceRefs.statsCrewBrescia2000],
  },
  {
    id: "sereni-pia-97",
    name: "Matteo Sereni",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 34,
    starts: 34,
    tag: "Porta Garilli",
    note: "Trentaquattro presenze e una salvezza costruita piu con i guanti che con i riflettori.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "tramezzani-pia-97",
    name: "Paolo Tramezzani",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 32,
    starts: 32,
    tag: "Fascia italiana",
    note: "Laterale da trentadue partenze: il tipo di carta che fa respirare un 4-4-2.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "delli-carri-pia-97",
    name: "Daniele Delli Carri",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 31,
    starts: 30,
    tag: "Stopper di provincia",
    note: "Trenta partenze, gomiti larghi e mestiere da salvezza vera.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "vierchowod-pia-97",
    name: "Pietro Vierchowod",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 88,
    goals: 2,
    appearances: 29,
    starts: 28,
    tag: "Zar al Garilli",
    note: "Trentotto anni, ventotto partenze e ancora duelli vinti col fisico.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "mazzola-pia-97",
    name: "Alessandro Mazzola",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Italia",
    score: 83,
    appearances: 30,
    starts: 30,
    tag: "Jolly sobrio",
    note: "Trenta presenze da titolare, nessun clamore e una utilita enorme per il draft.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "marcorossi-pia-97",
    name: "Marco Rossi",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 24,
    starts: 24,
    tag: "Marcatura antica",
    note: "Difensore di rotazione pesante, perfetto per abbassare il glamour e alzare la coerenza.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "scienza-pia-97",
    name: "Giuseppe Scienza",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 3,
    appearances: 31,
    starts: 29,
    tag: "Regia operaia",
    note: "Ventinove partenze, tre gol e quella tecnica nascosta da provincia anni 90.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "stroppa-pia-97",
    name: "Giovanni Stroppa",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    appearances: 20,
    starts: 18,
    tag: "Piede educato",
    note: "Qualita tecnica in un contesto ruvido: una carta da intesa piu che da rating puro.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "valoti-pia-97",
    name: "Aladino Valoti",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    appearances: 21,
    starts: 13,
    tag: "Mediano di scorta",
    note: "Rotazione utile, nome da almanacco e profilo perfetto per il lato sporco di Provincia.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "bordin-pia-97",
    name: "Roberto Bordin",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    appearances: 21,
    starts: 13,
    tag: "Cerniera",
    note: "Centrocampista da equilibrio, poche pose e tanta copertura preventiva.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "piovani-pia-97",
    name: "Giampiero Piovani",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["mezzala", "trequartista", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 5,
    appearances: 30,
    starts: 24,
    tag: "Punta raccordo",
    note: "Cinque gol, trenta presenze e abbastanza lavoro per non sembrare una scelta facile.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "dionigi-pia-97",
    name: "Davide Dionigi",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 5,
    appearances: 23,
    starts: 21,
    tag: "Nove di provincia",
    note: "Cinque gol e ventuno partenze: attaccante concreto per draft senza scorciatoie.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "rastelli-pia-97",
    name: "Massimo Rastelli",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 31,
    starts: 14,
    tag: "Cambio lungo",
    note: "Trentuno presenze, spesso da subentro: esattamente il tipo di opzione che rende un pacchetto meno ovvio.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "murgita-pia-97",
    name: "Roberto Murgita",
    club: "Piacenza",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 5,
    appearances: 27,
    starts: 14,
    tag: "Bomber di rotazione",
    note: "Cinque gol dalla seconda linea offensiva, una carta cult senza aura da superstar.",
    evidence: [evidenceRefs.bdfutbolPiacenza1997],
  },
  {
    id: "taibi-ven-98",
    name: "Massimo Taibi",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 86,
    appearances: 33,
    starts: 33,
    tag: "Guanti lagunari",
    note: "Trentatre presenze da titolare nella salvezza di Novellino: porta da provincia vera.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "dalcanto-ven-98",
    name: "Alessandro Dal Canto",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 27,
    starts: 26,
    tag: "Centrale arancioverde",
    note: "Difensore da ventisei partenze, utile per costruire una linea meno ovvia.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "pavan-ven-98",
    name: "Simone Pavan",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 26,
    starts: 25,
    tag: "Marcatura lagunare",
    note: "Ventisei presenze e tanti duelli sporchi per tenere in piedi il ribaltone salvezza.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "luppi-ven-98",
    name: "Gianluca Luppi",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 31,
    starts: 30,
    tag: "Esperienza dietro",
    note: "Trenta partenze, cartellini e mestiere: una carta da tenere quando il draft si sporca.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "brioschi-ven-98",
    name: "Emanuele Brioschi",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 83,
    appearances: 24,
    starts: 23,
    tag: "Jolly basso",
    note: "Laterale da ventitre partenze, ideale per far quadrare moduli e intesa.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "pedone-ven-98",
    name: "Francesco Pedone",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 33,
    starts: 31,
    tag: "Mediana da salvezza",
    note: "Trentuno partenze e tre gol: equilibrio, inserimenti e pochissima copertina.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "miceli-ven-98",
    name: "Salvatore Miceli",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 27,
    starts: 25,
    tag: "Polmoni in mezzo",
    note: "Venticinque partenze da centrocampo di lotta, perfetto per l'ossatura arancioverde.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "volpi-ven-98",
    name: "Sergio Volpi",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 27,
    starts: 25,
    tag: "Regia giovane",
    note: "Prima versione da regista: venticinque partenze e il seme di una carriera da cervello di Serie A.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "valtolina-ven-98",
    name: "Fabian Valtolina",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 3,
    appearances: 29,
    starts: 18,
    tag: "Fascia del Penzo",
    note: "Corsa laterale, tre gol e quel profilo da culto che tiene viva Provincia.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "maniero-ven-98",
    name: "Filippo Maniero",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 88,
    goals: 12,
    appearances: 27,
    starts: 25,
    tag: "Pippo in laguna",
    note: "Dodici gol e presenza da area: il terminale concreto prima e dopo l'arrivo del Chino.",
    evidence: [evidenceRefs.bdfutbolVenezia1998, evidenceRefs.statsCrewVenezia1998],
  },
  {
    id: "recoba-ven-98",
    name: "Alvaro Recoba",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Uruguay",
    score: 92,
    goals: 10,
    appearances: 19,
    starts: 19,
    tag: "Chino al Penzo",
    note: "Diciannove partite e dieci gol: la mezza stagione che trasforma una salvezza in leggenda.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "carnasciali-ven-98",
    name: "Daniele Carnasciali",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 16,
    starts: 16,
    tag: "Rotazione esperta",
    note: "Sedici partenze difensive, carta utile quando il draft chiede solidita prima del nome.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "iachini-ven-98",
    name: "Giuseppe Iachini",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    appearances: 15,
    starts: 15,
    tag: "Mediano ruvido",
    note: "Quindici partenze, tackle e voce: una carta da provincia prima della sua vita in panchina.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "schwoch-ven-98",
    name: "Stefan Schwoch",
    club: "Venezia",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 14,
    starts: 11,
    tag: "Punta cult",
    note: "Prima punta di rotazione, nome da provincia e ponte perfetto verso le storie di Serie B.",
    evidence: [evidenceRefs.bdfutbolVenezia1998],
  },
  {
    id: "taibi-reg-99",
    name: "Massimo Taibi",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 85,
    appearances: 18,
    starts: 18,
    tag: "Porta Granillo",
    note: "Diciotto partenze nella prima Reggina di Serie A: guanti, carattere e stadio bollente.",
    evidence: [evidenceRefs.bdfutbolReggina1999, evidenceRefs.statsCrewReggina1999],
  },
  {
    id: "orlandoni-reg-99",
    name: "Paolo Orlandoni",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 83,
    appearances: 13,
    starts: 13,
    tag: "Seconda porta",
    note: "Tredici presenze e minuti pesanti: profondita vera per una rosa da salvezza.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "stovini-reg-99",
    name: "Lorenzo Stovini",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 34,
    starts: 34,
    tag: "Muro amaranto",
    note: "Trentaquattro partite, sempre titolare: il tipo di difensore che regge un draft serio.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "cirillo-reg-99",
    name: "Bruno Cirillo",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 32,
    starts: 31,
    tag: "Bruno al Granillo",
    note: "Trentuno partenze, due gol e marcature senza sconti nella stagione del debutto.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "giacchetta-reg-99",
    name: "Simone Giacchetta",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Caposaldo amaranto",
    note: "Trenta presenze da titolare, una rete e la fascia destra che macina chilometri.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "morabito-reg-99",
    name: "Giovanni Morabito",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 84,
    appearances: 30,
    starts: 30,
    tag: "Fascia ionica",
    note: "Trenta partenze, spinta e copertura: profilo perfetto per la logica Provincia.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "baronio-reg-99",
    name: "Roberto Baronio",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 3,
    appearances: 31,
    starts: 30,
    tag: "Regia under",
    note: "Trenta partenze, tre gol e un piede da promessa in una squadra da battaglia.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "brevi-reg-99",
    name: "Ezio Brevi",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    appearances: 28,
    starts: 26,
    tag: "Mediana operaia",
    note: "Ventisei partenze, cartellini e copertura: carta grinder senza bisogno di folklore.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "pirlo-reg-99",
    name: "Andrea Pirlo",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "mezzala"],
    country: "Italia",
    score: 88,
    goals: 6,
    appearances: 28,
    starts: 19,
    tag: "Pirlo amaranto",
    note: "Prestito giovanissimo, sei gol e lampi da trequartista prima della regia totale.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "pralija-reg-99",
    name: "Nenad Pralija",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Croazia",
    score: 83,
    appearances: 23,
    starts: 16,
    tag: "Rotazione balcanica",
    note: "Centrocampo di esperienza e rotazione, utile per non trasformare ogni scelta in una leggenda.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "kallon-reg-99",
    name: "Mohamed Kallon",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Sierra Leone",
    score: 88,
    goals: 11,
    appearances: 30,
    starts: 28,
    tag: "Undici al debutto",
    note: "Undici gol nella prima Reggina di Serie A: velocita, istinto e Granillo in piedi.",
    evidence: [evidenceRefs.bdfutbolReggina1999, evidenceRefs.statsCrewReggina1999],
  },
  {
    id: "possanzini-reg-99",
    name: "Davide Possanzini",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 3,
    appearances: 31,
    starts: 24,
    tag: "Seconda punta",
    note: "Ventiquattro partenze, tre gol e lavoro tra le linee: una carta da incastro, non da copertina.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "cozza-reg-99",
    name: "Francesco Cozza",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 17,
    starts: 9,
    tag: "Dieci del Sud",
    note: "Minuti da trequartista e identita amaranto, perfetto per un pacchetto pieno di memoria.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "bogdani-reg-99",
    name: "Erjon Bogdani",
    club: "Reggina",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Albania",
    score: 83,
    goals: 2,
    appearances: 10,
    starts: 7,
    tag: "Torre giovane",
    note: "Sette partenze e due gol: profondita offensiva da almanacco e provincia vera.",
    evidence: [evidenceRefs.bdfutbolReggina1999],
  },
  {
    id: "chimenti-lec-99",
    name: "Antonio Chimenti",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 85,
    appearances: 33,
    starts: 33,
    tag: "Guanti salentini",
    note: "Trentatre presenze, Via del Mare caldo e una salvezza costruita anche tra i pali.",
    evidence: [evidenceRefs.bdfutbolLecce1999, evidenceRefs.statsCrewLecce1999],
  },
  {
    id: "balleri-lec-99",
    name: "Davide Balleri",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 84,
    appearances: 32,
    starts: 32,
    tag: "Fascia Via del Mare",
    note: "Trentadue partenze e tanta corsa laterale: scelta da Provincia piu che da copertina.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "savino-lec-99",
    name: "Alberto Savino",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Linea Cavasin",
    note: "Trentuno partenze, un gol e difesa di mestiere in una squadra appena salita.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "juarez-lec-99",
    name: "Juarez",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Brasile",
    score: 85,
    appearances: 33,
    starts: 32,
    tag: "Centrale brasiliano",
    note: "Trentadue partenze, fisico e anticipo: un difensore cult del Lecce di fine millennio.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "viali-lec-99",
    name: "William Viali",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 32,
    starts: 32,
    tag: "Trentadue su trentadue",
    note: "Sempre titolare, un gol e zero glamour: esattamente il mattone giusto per Provincia.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "pivotto-lec-99",
    name: "Matteo Pivotto",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["mediano", "centrale", "braccetto"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 27,
    starts: 20,
    tag: "Jolly basso",
    note: "Difensore di rotazione lunga, utile quando il draft chiede copertura e non solo rating.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "conticchio-lec-99",
    name: "Alessandro Conticchio",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    goals: 4,
    appearances: 33,
    starts: 32,
    tag: "Mezzala salentina",
    note: "Trentadue partenze, quattro gol e chilometri veri nel centrocampo di Cavasin.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "lima-lec-99",
    name: "Francisco Lima",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Brasile",
    score: 86,
    goals: 1,
    appearances: 32,
    starts: 32,
    tag: "Dieci operaio",
    note: "Brasiliano da trentadue partenze: tecnica, fisico e cartellini da centrocampo anni 90.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "piangerelli-lec-99",
    name: "Luigi Piangerelli",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    appearances: 29,
    starts: 24,
    tag: "Cerniera giallorossa",
    note: "Ventiquattro partenze e molto lavoro oscuro: una carta da equilibrio puro.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "lucarelli-lec-99",
    name: "Cristiano Lucarelli",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 15,
    appearances: 30,
    starts: 29,
    tag: "Quindici al Via del Mare",
    note: "Quindici gol in una neopromossa: centravanti da culto prima dell'icona livornese.",
    evidence: [evidenceRefs.bdfutbolLecce1999, evidenceRefs.statsCrewLecce1999],
  },
  {
    id: "sesa-lec-99",
    name: "David Sesa",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Svizzera",
    score: 86,
    goals: 7,
    assists: 5,
    appearances: 29,
    starts: 27,
    tag: "Svizzerino creativo",
    note: "Sette gol e cinque assist: la carta che collega fatica salentina e fantasia.",
    evidence: [evidenceRefs.bdfutbolLecce1999, evidenceRefs.statsCrewLecce1999],
  },
  {
    id: "colonnello-lec-99",
    name: "Gianluca Colonnello",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 83,
    appearances: 27,
    starts: 14,
    tag: "Rotazione di fascia",
    note: "Ventisette presenze dalla fascia: profondita e flessibilita per non forzare sempre i titolari.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "bonomi-lec-99",
    name: "Claudio Bonomi",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 83,
    goals: 3,
    appearances: 24,
    starts: 8,
    tag: "Cambio offensivo",
    note: "Tre gol dalla rotazione, perfetto per rendere i pacchetti meno automatici.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "biliotti-lec-99",
    name: "Emiliano Biliotti",
    club: "Lecce",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 82,
    appearances: 19,
    starts: 6,
    tag: "Jolly da panchina",
    note: "Diciannove presenze da incastro, utile per abbassare il glamour senza perdere ruolo.",
    evidence: [evidenceRefs.bdfutbolLecce1999],
  },
  {
    id: "balli-sal-98",
    name: "Daniele Balli",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 31,
    starts: 31,
    tag: "Porta dell'Arechi",
    note: "Trentuno presenze e dieci vittorie: il portiere della Salernitana tornata in A dopo una vita.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "delgrosso-sal-98",
    name: "Alessandro Del Grosso",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Terzino dell'Arechi",
    note: "Trentuno partenze e un gol: fascia vera in una squadra finita a un punto dalla salvezza.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "tosto-sal-98",
    name: "Vittorio Tosto",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 83,
    appearances: 27,
    starts: 19,
    tag: "Fascia granata",
    note: "Ventisette presenze e corsa larga: una carta da equilibrio piu che da copertina.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "fresi-sal-98",
    name: "Salvatore Fresi",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Italia",
    score: 86,
    goals: 3,
    appearances: 27,
    starts: 25,
    tag: "Fresi granata",
    note: "Prestito pesante dall'Inter, tre gol e presenza da leader in una difesa piena di carattere.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "fusco-sal-98",
    name: "Luca Fusco",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 24,
    starts: 23,
    tag: "Linea granata",
    note: "Ventitre partenze, cartellini e marcature: difensore perfetto per una run ruvida.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "bolic-sal-98",
    name: "Drazen Bolic",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Croazia",
    score: 84,
    goals: 1,
    appearances: 31,
    starts: 25,
    tag: "Centrale balcanico",
    note: "Trentuno presenze e un gol: fisico balcanico nella Salernitana piu amara.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "breda-sal-98",
    name: "Roberto Breda",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 26,
    starts: 25,
    tag: "Capitano mediano",
    note: "Venticinque partenze, due gol e il passo da capitano di provincia.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "gattuso-sal-98",
    name: "Gennaro Gattuso",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 88,
    appearances: 25,
    starts: 24,
    tag: "Ringhio ventenne",
    note: "Ritorno dalla Scozia, ventiquattro partenze e gia tutto il fuoco che poi finira al Milan.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "vannucchi-sal-98",
    name: "Ighli Vannucchi",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 31,
    starts: 18,
    tag: "Trequarti granata",
    note: "Trentuno presenze, tre gol e tecnica tra le linee in mezzo alla corsa salernitana.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "divaio-sal-98",
    name: "Marco Di Vaio",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 12,
    assists: 6,
    appearances: 31,
    starts: 29,
    tag: "Dodici all'Arechi",
    note: "Miglior marcatore granata: dodici gol e sei assist in una retrocessione decisa per un punto.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "giampaolo-sal-98",
    name: "Federico Giampaolo",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 84,
    goals: 3,
    assists: 4,
    appearances: 24,
    starts: 17,
    tag: "Seconda punta",
    note: "Tre gol, quattro assist e nome da almanacco: perfetto per non pescare solo star.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "bernardini-sal-98",
    name: "Antonino Bernardini",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 20,
    starts: 18,
    tag: "Mediana nascosta",
    note: "Diciotto partenze e un gol: carta operaia per dare peso alla panchina del draft.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998],
  },
  {
    id: "dimichele-sal-98",
    name: "David Di Michele",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 26,
    starts: 11,
    tag: "Di Michele giovane",
    note: "Ventisei presenze e tre gol prima di diventare una faccia ricorrente della Serie A di provincia.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "song-sal-98",
    name: "Rigobert Song",
    club: "Salernitana",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Camerun",
    score: 84,
    goals: 1,
    appearances: 4,
    starts: 4,
    tag: "Quattro presenze leggenda",
    note: "Solo quattro partite, un gol all'Olimpico e poi Liverpool: easter egg puro da busta amarcord.",
    evidence: [evidenceRefs.bdfutbolSalernitana1998, evidenceRefs.statsCrewSalernitana1998],
  },
  {
    id: "roccati-emp-97",
    name: "Marco Roccati",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 83,
    appearances: 28,
    starts: 27,
    tag: "Porta del Castellani",
    note: "Ventisette partenze nella salvezza di Spalletti: carta da provincia vera, senza riflettori.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "pfusco-emp-97",
    name: "Pietro Fusco",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 31,
    starts: 31,
    tag: "Mediana operaia",
    note: "Trentuno presenze da equilibrio: esattamente il tipo di carta che evita draft solo da copertina.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "pane-emp-97",
    name: "Alessandro Pane",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 33,
    starts: 32,
    tag: "Linea del Castellani",
    note: "Trentadue partenze e due gol: difensore di provincia con peso vero nella salvezza.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "bianconi-emp-97",
    name: "Stefano Bianconi",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 32,
    starts: 32,
    tag: "Centrale fisso",
    note: "Trentadue partenze, tanta marcatura e zero glamour: una base perfetta per Provincia.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "baldini-emp-97",
    name: "Daniele Baldini",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 30,
    starts: 29,
    tag: "Difesa spallettiana",
    note: "Ventinove partenze e un gol: esperienza e ruvidita da domenica pomeriggio.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "ficini-emp-97",
    name: "Fabrizio Ficini",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    appearances: 32,
    starts: 31,
    tag: "Filo del centrocampo",
    note: "Trentuno partenze nate a Empoli: una carta da identita locale e lavoro sporco.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "ametrano-emp-97",
    name: "Raffaele Ametrano",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 29,
    starts: 27,
    tag: "Strappo in mediana",
    note: "Ventinove presenze, tre gol e scuola Juventus: esterno da amarcord del Castellani.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "tonetto-emp-97",
    name: "Max Tonetto",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "DIF"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 30,
    starts: 26,
    tag: "Fascia sinistra",
    note: "Tre gol e tanta fascia prima delle versioni piu note: una pesca nostalgica intelligente.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "martusciello-emp-97",
    name: "Giovanni Martusciello",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Italia",
    score: 86,
    goals: 6,
    appearances: 23,
    starts: 20,
    tag: "Dieci del Castellani",
    note: "Sei gol e palla cucita al piede: memoria empolese prima che il nome diventasse panchina.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "esposito-emp-97",
    name: "Carmine Esposito",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 88,
    goals: 14,
    appearances: 32,
    starts: 31,
    tag: "Quattordici al Castellani",
    note: "Quattordici gol nella Serie A di Spalletti: bomber cult, non icona mainstream.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "cappellini-emp-97",
    name: "Massimiliano Cappellini",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 9,
    appearances: 26,
    starts: 22,
    tag: "Seconda punta cult",
    note: "Nove gol, tecnica e provincia anni 90: compagno ideale per non isolare Esposito.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "florijancic-emp-97",
    name: "Matjaz Florijancic",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Slovenia",
    score: 84,
    goals: 6,
    appearances: 28,
    starts: 11,
    tag: "Punta da almanacco",
    note: "Sei gol dalla rotazione: una di quelle carte che rendono il pacchetto meno ovvio.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "bonomi-emp-97",
    name: "Claudio Bonomi",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 82,
    goals: 3,
    appearances: 19,
    starts: 18,
    tag: "Rotazione con gol",
    note: "Diciotto partenze e tre gol: carta utile per dare logica ai pacchetti intermedi.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "bettella-emp-97",
    name: "Stefano Bettella",
    club: "Empoli",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 81,
    goals: 1,
    appearances: 21,
    starts: 7,
    tag: "Cambio difensivo",
    note: "Ventuno presenze e un gol: profondita vera per far respirare il blocco Empoli.",
    evidence: [evidenceRefs.bdfutbolEmpoli1997, evidenceRefs.footballSquadsEmpoli1997],
  },
  {
    id: "mancini-fog-91",
    name: "Francesco Mancini",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    appearances: 30,
    starts: 30,
    tag: "Porta zemaniana",
    note: "Trenta partenze in una squadra da 58 gol fatti e 58 subiti: portiere per chi ama il rischio.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "codispoti-fog-91",
    name: "Maurizio Codispoti",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 34,
    starts: 33,
    tag: "Linea alta",
    note: "Trentaquattro presenze e due gol nella difesa piu esposta e divertente della Serie A.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "consagra-fog-91",
    name: "Angelo Consagra",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    appearances: 29,
    starts: 28,
    tag: "Copertura rossonera",
    note: "Ventotto partenze nel calcio totale del boemo: meno nome, tanta struttura per il draft.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "matrecano-fog-91",
    name: "Salvatore Matrecano",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 28,
    starts: 27,
    tag: "Marcatore giovane",
    note: "Ventisette partenze da ventunenne: anticipo e coraggio in campo aperto.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "grandini-fog-91",
    name: "Gualtiero Grandini",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 82,
    appearances: 17,
    starts: 16,
    tag: "Cambio di linea",
    note: "Sedici partenze e parecchi minuti sporchi: carta di profondita per non pescare solo tridente.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "barone-fog-91",
    name: "Onofrio Barone",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["CEN", "DIF"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Italia",
    score: 85,
    appearances: 34,
    starts: 34,
    tag: "Polmone totale",
    note: "Trentaquattro su trentaquattro: il tipo di carta operaia che tiene in piedi Zemanlandia.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "shalimov-fog-91",
    name: "Igor Shalimov",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Russia",
    score: 90,
    goals: 9,
    appearances: 33,
    starts: 33,
    tag: "Russia allo Zaccheria",
    note: "Nove gol e trentatre partenze: talento sovietico nel laboratorio piu folle del campionato.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "petrescu-fog-91",
    name: "Dan Petrescu",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Romania",
    score: 87,
    goals: 4,
    appearances: 25,
    starts: 24,
    tag: "Fascia romena",
    note: "Quattro gol dalla corsia e uno stile gia europeo: jolly perfetto per una busta amarcord.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "baiano-fog-91",
    name: "Francesco Baiano",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 16,
    appearances: 33,
    starts: 33,
    tag: "Sedici di Zeman",
    note: "Sedici gol e tridente acceso: la scelta forte senza trasformare Provincia in una modalità solo iconica.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "rambaudi-fog-91",
    name: "Roberto Rambaudi",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 88,
    goals: 9,
    appearances: 33,
    starts: 32,
    tag: "Ala di Zemanlandia",
    note: "Nove gol, corsa e tagli continui: la terza faccia del tridente Baiano-Signori-Rambaudi.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "signori-fog-91",
    name: "Giuseppe Signori",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 90,
    goals: 11,
    appearances: 32,
    starts: 31,
    tag: "Beppe prima di Lazio",
    note: "Undici gol prima della consacrazione laziale: vietato pescarlo due volte nello stesso draft.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "picasso-fog-91",
    name: "Mauro Picasso",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 20,
    starts: 19,
    tag: "Mediano da quadro",
    note: "Nome da museo, diciannove partenze e un gol: dettaglio perfetto per una run meno ovvia.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "porro-fog-91",
    name: "Alessandro Porro",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 82,
    goals: 1,
    appearances: 21,
    starts: 8,
    tag: "Rotazione larga",
    note: "Ventuno presenze dalla rotazione: utile per tenere i pacchetti vivi anche fuori dalle star.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "padalino-fog-91",
    name: "Pasquale Padalino",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 15,
    starts: 12,
    tag: "Padalino giovane",
    note: "Dodici partenze e un gol a diciannove anni: altro easter egg da provincia rossonera.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "kolyvanov-fog-91",
    name: "Igor Kolyvanov",
    club: "Foggia",
    season: "1991-92",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Russia",
    score: 84,
    goals: 3,
    appearances: 15,
    starts: 5,
    tag: "Punta sovietica",
    note: "Tre gol in rotazione: un altro tassello dell'album russo-satanello di inizio anni 90.",
    evidence: [evidenceRefs.bdfutbolFoggia1991],
  },
  {
    id: "pagliuca-sam-88",
    name: "Gianluca Pagliuca",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 90,
    appearances: 33,
    starts: 33,
    tag: "Guanti pre-scudetto",
    note: "Trentatre presenze e una difesa da venticinque gol subiti nella Samp che vince la Coppa Italia.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "mannini-sam-88",
    name: "Moreno Mannini",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 84,
    appearances: 18,
    starts: 17,
    tag: "Fascia in crescita",
    note: "Laterale gia dentro la macchina di Boskov prima della maturazione scudetto.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "vierchowod-sam-88",
    name: "Pietro Vierchowod",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 91,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Zar in coppa",
    note: "Ventinove presenze in A e gol anche in Europa: il muro doriano prima dello scudetto.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "pellegrini-sam-88",
    name: "Luca Pellegrini",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    appearances: 24,
    starts: 24,
    tag: "Linea blucerchiata",
    note: "Ventiquattro presenze in campionato per dare continuita alla retroguardia.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "carboni-sam-88",
    name: "Amedeo Carboni",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 31,
    starts: 30,
    tag: "Sinistra europea",
    note: "Trentuno presenze e spinta da terzino nel primo anno blucerchiato.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "victor-sam-88",
    name: "Victor Munoz",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Spagna",
    score: 87,
    goals: 2,
    appearances: 31,
    starts: 31,
    tag: "Barca a Marassi",
    note: "Internazionale spagnolo da trentuno presenze, equilibrio e due gol nella mediana di Boskov.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "pari-sam-88",
    name: "Fausto Pari",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Cerniera doriana",
    note: "Trenta presenze in A e lavoro continuo a protezione del talento davanti.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "cerezo-sam-88",
    name: "Toninho Cerezo",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Brasile",
    score: 90,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Classe brasileira",
    note: "Regia e tempi in una Samp che impara a vincere finali e partite europee.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "dossena-sam-88",
    name: "Giuseppe Dossena",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 88,
    goals: 5,
    appearances: 34,
    starts: 32,
    tag: "Mondiale in mediana",
    note: "Trentaquattro presenze e cinque gol: innesto pesante nella stagione della Coppa Italia.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "salsano-sam-88",
    name: "Fausto Salsano",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["mezzala", "trequartista"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 29,
    starts: 14,
    tag: "Cambio creativo",
    note: "Ventinove presenze e minuti offensivi per allargare le scelte oltre i gemelli del gol.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "mancini-sam-88",
    name: "Roberto Mancini",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 9,
    appearances: 29,
    starts: 28,
    tag: "Mancio da coppa",
    note: "Nove gol in campionato e firma nella Coppa Italia che prepara la Samp dei primi anni 90.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "vialli-sam-88",
    name: "Gianluca Vialli",
    club: "Sampdoria",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 93,
    goals: 14,
    appearances: 30,
    starts: 30,
    tag: "Quattordici da lancio",
    note: "Quattordici gol in Serie A e trentadue complessivi: la stagione dell'esplosione internazionale.",
    evidence: [evidenceRefs.manualSampdoria1988],
  },
  {
    id: "vialli-sam-90",
    name: "Gianluca Vialli",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 95,
    goals: 19,
    appearances: 26,
    starts: 26,
    tag: "Gemello del gol",
    note: "Perfetto per l'easter egg Vialli-Mancini.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "mancini-sam-90",
    name: "Roberto Mancini",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 92,
    goals: 12,
    appearances: 30,
    starts: 29,
    tag: "Classe doriana",
    note: "Sblocca sinergia se affiancato a Vialli.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "batistuta-fio-98",
    name: "Gabriel Batistuta",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 96,
    goals: 21,
    appearances: 28,
    starts: 28,
    tag: "Batigol",
    note: "Cannonate Nintendo nel sogno scudetto viola di Trapattoni.",
    evidence: [evidenceRefs.statsCrewFiorentina1998, evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "ronaldo-inter-97",
    name: "Ronaldo",
    club: "Inter",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Brasile",
    score: 99,
    goals: 25,
    appearances: 32,
    tag: "Il Fenomeno",
    note: "Picco arcade altissimo, ideale come scelta rara.",
  },
  {
    id: "buffon-parma-98",
    name: "Gianluigi Buffon",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 96,
    appearances: 34,
    starts: 34,
    tag: "Parma europeo",
    note: "Parte del nucleo Buffon-Thuram-Cannavaro-Crespo.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "thuram-parma-98",
    name: "Lilian Thuram",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "terzino"],
    country: "Francia",
    score: 94,
    assists: 3,
    appearances: 34,
    starts: 34,
    tag: "Muro gialloblu",
    note: "Difensore d'elite per il blocco Parma.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "crespo-parma-98",
    name: "Hernan Crespo",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 93,
    goals: 16,
    assists: 3,
    appearances: 30,
    starts: 29,
    tag: "Coppa e cinismo",
    note: "Attaccante da prompt nostalgia Coppa UEFA.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "nesta-laz-99",
    name: "Alessandro Nesta",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 97,
    appearances: 28,
    tag: "Capitano scudetto",
    note: "Pilastro della Lazio dello scudetto.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "veron-laz-99",
    name: "Juan Sebastian Veron",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Argentina",
    score: 95,
    goals: 8,
    assists: 11,
    appearances: 31,
    tag: "Regia verticale",
    note: "Centrocampo tecnico per alzare il record finale.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "nedved-laz-99",
    name: "Pavel Nedved",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Repubblica Ceca",
    score: 94,
    goals: 5,
    appearances: 28,
    tag: "Corsa infinita",
    note: "Jolly ad alta energia nelle mode scudetto.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "totti-roma-00",
    name: "Francesco Totti",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 98,
    goals: 13,
    appearances: 30,
    starts: 30,
    tag: "Er Pupone",
    note: "Sblocca triangolo scudetto con Batistuta o Montella.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "batistuta-roma-00",
    name: "Gabriel Batistuta",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 94,
    goals: 20,
    appearances: 28,
    starts: 28,
    tag: "Colpo scudetto",
    note: "Una scelta da titolo nel modo 2000.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "cafu-roma-00",
    name: "Cafu",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Brasile",
    score: 95,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Pendolino",
    note: "Spinta brasiliana nella Roma scudetto.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "buffon-juv-01",
    name: "Gianluigi Buffon",
    club: "Juventus",
    season: "2001-02",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 97,
    tag: "Numero 1",
    note: "Base sicura per inseguire lo 38-0.",
  },
  {
    id: "maldini-mil-03",
    name: "Paolo Maldini",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 98,
    appearances: 30,
    starts: 30,
    tag: "Eleganza totale",
    note: "Difensore premium nel blocco Milan europeo.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "dida-mil-03",
    name: "Dida",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Brasile",
    score: 93,
    appearances: 32,
    starts: 32,
    tag: "Porta rossonera",
    note: "Riflessi e rigori per il Milan europeo.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "pirlo-mil-03",
    name: "Andrea Pirlo",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 96,
    goals: 6,
    assists: 7,
    appearances: 32,
    starts: 31,
    tag: "Geometra",
    note: "Alza controllo e creativita della simulazione.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "sheva-mil-03",
    name: "Andriy Shevchenko",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Ucraina",
    score: 97,
    goals: 24,
    assists: 3,
    appearances: 32,
    starts: 30,
    tag: "Pallone d'Oro",
    note: "Finalizzazione da record.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "kaka-mil-03",
    name: "Kaka",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Brasile",
    score: 95,
    goals: 10,
    assists: 5,
    appearances: 30,
    starts: 25,
    tag: "Ricky al debutto",
    note: "Strappi da trequartista nello scudetto rossonero.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "kaka-mil-06",
    name: "Kaka",
    club: "Milan",
    season: "2006-07",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Brasile",
    score: 98,
    tag: "Strappo verticale",
    note: "Jolly offensivo per squadre tecniche.",
  },
  {
    id: "zlatan-inter-08",
    name: "Zlatan Ibrahimovic",
    club: "Inter",
    season: "2008-09",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Svezia",
    score: 96,
    goals: 25,
    tag: "Scudetto potente",
    note: "Dominanza fisica nel finale anni 2000.",
  },
  {
    id: "adriano-inter-04",
    name: "Adriano",
    club: "Inter",
    season: "2004-05",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Brasile",
    score: 94,
    goals: 16,
    tag: "Imperatore",
    note: "Sinistro devastante per un easter egg globale.",
  },
  {
    id: "zanetti-inter-09",
    name: "Javier Zanetti",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Argentina",
    score: 95,
    appearances: 37,
    starts: 37,
    tag: "Triplete",
    note: "Versatilita perfetta per coprire buchi nell'XI.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "sneijder-inter-09",
    name: "Wesley Sneijder",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Paesi Bassi",
    score: 95,
    goals: 4,
    appearances: 26,
    starts: 24,
    tag: "Treble trigger",
    note: "Regia offensiva per il modo derby.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "juliocesar-inter-09",
    name: "Julio Cesar",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Brasile",
    score: 94,
    appearances: 38,
    starts: 38,
    tag: "Triplete brasileiro",
    note: "Portiere da notte europea e base solidissima.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "maicon-inter-09",
    name: "Maicon",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Brasile",
    score: 94,
    goals: 7,
    appearances: 33,
    starts: 33,
    tag: "Colosso",
    note: "Terzino totale per spingere la simulazione.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "milito-inter-09",
    name: "Diego Milito",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 96,
    goals: 22,
    appearances: 35,
    starts: 33,
    tag: "Principe del Triplete",
    note: "Finalizzatore da scudetto e notti decisive.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "etoo-inter-09",
    name: "Samuel Eto'o",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["ala", "seconda-punta"],
    country: "Camerun",
    score: 94,
    goals: 12,
    appearances: 32,
    starts: 27,
    tag: "Campione sacrificato",
    note: "Ala, punta, terzino aggiunto: carta da equilibrio totale.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "lucio-inter-09",
    name: "Lucio",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Brasile",
    score: 93,
    goals: 1,
    appearances: 31,
    starts: 30,
    tag: "Centrale in conduzione",
    note: "Difensore che rompe la linea e porta palla fuori pressione.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "pandev-inter-09",
    name: "Goran Pandev",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Macedonia del Nord",
    score: 89,
    goals: 3,
    appearances: 19,
    starts: 13,
    tag: "Jolly di gennaio",
    note: "Carta elastica per completare il rombo e non spezzare l'intesa.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "dinatale-udi-10",
    name: "Antonio Di Natale",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 95,
    goals: 28,
    assists: 6,
    appearances: 36,
    starts: 35,
    tag: "Totogoal",
    note: "Easter egg Udinese: se lo prendi, la provincia sogna.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "pastore-pal-09",
    name: "Javier Pastore",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Argentina",
    score: 91,
    goals: 3,
    appearances: 34,
    starts: 27,
    tag: "Barbera nights",
    note: "El Flaco nel Palermo di Zamparini: talento preso prima degli altri.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "hamsik-nap-12",
    name: "Marek Hamsik",
    club: "Napoli",
    season: "2012-13",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Slovacchia",
    score: 94,
    tag: "Cresta azzurra",
    note: "Centrocampista bandiera del Napoli moderno.",
  },
  {
    id: "vidal-juv-13",
    name: "Arturo Vidal",
    club: "Juventus",
    season: "2013-14",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Cile",
    score: 95,
    goals: 11,
    tag: "Guerriero",
    note: "Pressing, gol, recuperi: scoring molto bilanciato.",
  },
  {
    id: "pogba-juv-15",
    name: "Paul Pogba",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Francia",
    score: 94,
    goals: 8,
    assists: 12,
    appearances: 35,
    tag: "Octopus",
    note: "Centrocampo spettacolare per un draft tecnico.",
    evidence: [evidenceRefs.juveFc2015, evidenceRefs.topScorers2015],
  },
  {
    id: "dybala-juv-15",
    name: "Paulo Dybala",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Argentina",
    score: 94,
    goals: 19,
    assists: 9,
    appearances: 34,
    tag: "Joya nascente",
    note: "Prima Juve da dieci moderno: gol, assist e mancino stretto.",
    evidence: [evidenceRefs.juveFc2015, evidenceRefs.topScorers2015],
  },
  {
    id: "mandzukic-juv-15",
    name: "Mario Mandzukic",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Croazia",
    score: 91,
    goals: 10,
    assists: 4,
    appearances: 27,
    tag: "Pressing da nove",
    note: "Centravanti fisico che alza lavoro sporco e profondita.",
    evidence: [evidenceRefs.juveFc2015, evidenceRefs.topScorers2015],
  },
  {
    id: "barzagli-juv-15",
    name: "Andrea Barzagli",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 92,
    appearances: 31,
    tag: "BBC silenziosa",
    note: "Scelta difensiva pulita: pochi highlight, tantissima solidita.",
    evidence: [evidenceRefs.juveFc2015],
  },
  {
    id: "marchisio-juv-15",
    name: "Claudio Marchisio",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala", "mediano"],
    country: "Italia",
    score: 91,
    appearances: 23,
    tag: "Principino",
    note: "Regia elegante per legare BBC, Pogba e la Joya.",
    evidence: [evidenceRefs.juveFc2015],
  },
  {
    id: "alexsandro-juv-15",
    name: "Alex Sandro",
    club: "Juventus",
    season: "2015-16",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Brasile",
    score: 89,
    goals: 2,
    assists: 3,
    appearances: 22,
    tag: "Spinta mancina",
    note: "Terzino da rotazione premium, utile per aprire il lato sinistro.",
    evidence: [evidenceRefs.juveFc2015],
  },
  {
    id: "icardi-inter-17",
    name: "Mauro Icardi",
    club: "Inter",
    season: "2017-18",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 93,
    goals: 29,
    tag: "Area di rigore",
    note: "Scelta ad alto gol, bassa poesia, molto efficace.",
  },
  {
    id: "immobile-laz-19",
    name: "Ciro Immobile",
    club: "Lazio",
    season: "2019-20",
    era: "moderna",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 96,
    goals: 36,
    tag: "Scarpa d'Oro",
    note: "Picco realizzativo moderno.",
  },
  {
    id: "lukaku-inter-20",
    name: "Romelu Lukaku",
    club: "Inter",
    season: "2020-21",
    era: "moderna",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Belgio",
    score: 95,
    goals: 24,
    tag: "Lu-La",
    note: "Potenza e assist nel blocco scudetto Inter.",
  },
  {
    id: "barella-inter-20",
    name: "Nicolo Barella",
    club: "Inter",
    season: "2020-21",
    era: "moderna",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 93,
    tag: "Motore",
    note: "Equilibrio moderno per centrocampo a tre.",
  },
  {
    id: "leao-mil-21",
    name: "Rafael Leao",
    club: "Milan",
    season: "2021-22",
    era: "moderna",
    roles: ["ATT"],
    positionTags: ["ala", "seconda-punta"],
    country: "Portogallo",
    score: 94,
    tag: "Strappo scudetto",
    note: "Velocita pura nella fascia sinistra offensiva.",
  },
  {
    id: "maignan-mil-21",
    name: "Mike Maignan",
    club: "Milan",
    season: "2021-22",
    era: "moderna",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Francia",
    score: 94,
    tag: "Magic Mike",
    note: "Portiere moderno per costruzione e clean sheet.",
  },
  {
    id: "kvara-nap-22",
    name: "Khvicha Kvaratskhelia",
    club: "Napoli",
    season: "2022-23",
    era: "moderna",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Georgia",
    score: 95,
    tag: "Kvaradona",
    note: "Easter egg nome se affiancato a Osimhen.",
  },
  {
    id: "osimhen-nap-22",
    name: "Victor Osimhen",
    club: "Napoli",
    season: "2022-23",
    era: "moderna",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Nigeria",
    score: 96,
    goals: 26,
    tag: "Maschera scudetto",
    note: "Finalizzatore principe del Napoli 2022-23.",
  },
  {
    id: "lookman-ata-23",
    name: "Ademola Lookman",
    club: "Atalanta",
    season: "2023-24",
    era: "moderna",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Nigeria",
    score: 92,
    tag: "Dea europea",
    note: "Scelta moderna per una modalita outsider.",
  },
  {
    id: "gollini-ata-18",
    name: "Pierluigi Gollini",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 86,
    appearances: 20,
    starts: 20,
    tag: "Porta da Champions",
    note: "La porta della Dea che entra per la prima volta nell'Europa grande.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "hateboer-ata-18",
    name: "Hans Hateboer",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Paesi Bassi",
    score: 87,
    goals: 5,
    appearances: 35,
    starts: 33,
    tag: "Quinto feroce",
    note: "Corsa totale sulla fascia nel sistema uomo contro uomo.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "gosens-ata-18",
    name: "Robin Gosens",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Germania",
    score: 88,
    goals: 3,
    appearances: 28,
    starts: 23,
    tag: "Fascia europea",
    note: "La freccia sinistra del laboratorio Gasperini.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "mancini-ata-18",
    name: "Gianluca Mancini",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    goals: 5,
    appearances: 30,
    starts: 25,
    tag: "Braccetto goleador",
    note: "Difensore che aggredisce alto e segna come un attaccante aggiunto.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "palomino-ata-18",
    name: "Jose Luis Palomino",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Argentina",
    score: 86,
    goals: 1,
    appearances: 30,
    starts: 25,
    tag: "Duelli nerazzurri",
    note: "Marcatura aggressiva e anticipo continuo nella difesa a tre.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "freuler-ata-18",
    name: "Remo Freuler",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Svizzera",
    score: 88,
    goals: 2,
    appearances: 35,
    starts: 35,
    tag: "Regia verticale",
    note: "Equilibrio e inserimenti nella mediana che manda la Dea in Champions.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "papugomez-ata-18",
    name: "Papu Gomez",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta", "ala"],
    country: "Argentina",
    score: 93,
    goals: 7,
    appearances: 35,
    starts: 34,
    tag: "Capitano della Dea",
    note: "Trequartista basso, alto, laterale: il caos buono del sistema.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "deroon-ata-18",
    name: "Marten de Roon",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Paesi Bassi",
    score: 87,
    goals: 2,
    appearances: 35,
    starts: 34,
    tag: "Pressing olandese",
    note: "Schermo, aggressione e seconda palla: il cemento della Dea.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "pasalic-ata-18",
    name: "Mario Pasalic",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["CEN", "ATT"],
    positionTags: ["mezzala", "trequartista", "seconda-punta"],
    country: "Croazia",
    score: 86,
    goals: 5,
    appearances: 33,
    starts: 17,
    tag: "Inserimenti croati",
    note: "Rotazione pesante e gol da mezzala offensiva.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "zapata-ata-18",
    name: "Duvan Zapata",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Colombia",
    score: 94,
    goals: 23,
    appearances: 37,
    starts: 32,
    tag: "Peso Champions",
    note: "Il centravanti che trasforma la Dea in una macchina da gol.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "ilicic-ata-18",
    name: "Josip Ilicic",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Slovenia",
    score: 92,
    goals: 12,
    appearances: 31,
    starts: 23,
    tag: "Mancino irreale",
    note: "Tagli, finte lente e colpi che sembrano arrivare da un altro ritmo.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "toloi-ata-18",
    name: "Rafael Toloi",
    club: "Atalanta",
    season: "2018-19",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Brasile",
    score: 86,
    goals: 1,
    appearances: 21,
    starts: 20,
    tag: "Braccetto brasiliano",
    note: "Uscite palla al piede e marcature preventive nel sistema Gasperini.",
    evidence: [evidenceRefs.bdfutbolAtalanta2018, evidenceRefs.statsCrewAtalanta2018],
  },
  {
    id: "calafiori-bol-23",
    name: "Riccardo Calafiori",
    club: "Bologna",
    season: "2023-24",
    era: "moderna",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 89,
    tag: "Jolly difensivo",
    note: "Difensore moderno per un prompt outsider.",
  },
  {
    id: "sterchele-bol-97",
    name: "Giorgio Sterchele",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 85,
    appearances: 32,
    starts: 32,
    tag: "Porta rossoblu",
    note: "Il portiere del Bologna di Baggio, Intertoto e domeniche da VHS.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "paramatti-bol-97",
    name: "Michele Paramatti",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 87,
    goals: 5,
    assists: 3,
    appearances: 28,
    starts: 28,
    tag: "Sinistra rossoblu",
    note: "Terzino goleador e connettore per il blocco del Divin Codino.",
    evidence: [evidenceRefs.bdfutbolBologna1997, evidenceRefs.transfermarktBologna1997],
  },
  {
    id: "mangone-bol-97",
    name: "Amedeo Mangone",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 32,
    starts: 30,
    tag: "Linea Ulivieri",
    note: "Centrale affidabile dietro a un attacco pieno di fantasia.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "paganin-bol-97",
    name: "Massimo Paganin",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 31,
    starts: 28,
    tag: "Esperienza dietro",
    note: "Difensore da Serie A ruvida, utile per dare struttura al draft.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "torrisi-bol-97",
    name: "Stefano Torrisi",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 84,
    assists: 3,
    appearances: 24,
    starts: 23,
    tag: "Marcatura e lancio",
    note: "Carta di profondita per completare una difesa rossoblu coerente.",
    evidence: [evidenceRefs.bdfutbolBologna1997, evidenceRefs.transfermarktBologna1997],
  },
  {
    id: "marocchi-bol-97",
    name: "Giancarlo Marocchi",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Regia rossoblu",
    note: "Veterano che tiene insieme corsa, ritmo e palloni per Baggio.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "magoni-bol-97",
    name: "Oscar Magoni",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 84,
    appearances: 32,
    starts: 30,
    tag: "Mediana operaia",
    note: "Equilibrio e chilometri nel Bologna che torna europeo.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "nervo-bol-97",
    name: "Carlo Nervo",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 33,
    starts: 27,
    tag: "Strappi sulla fascia",
    note: "Ala di lavoro per collegare centrocampo e tridente.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "andersson-bol-97",
    name: "Kennet Andersson",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Svezia",
    score: 90,
    goals: 12,
    assists: 7,
    appearances: 32,
    starts: 31,
    tag: "Torre svedese",
    note: "Spalla fisica e assist-man per il Bologna del Codino.",
    evidence: [evidenceRefs.bdfutbolBologna1997, evidenceRefs.transfermarktBologna1997],
  },
  {
    id: "baggio-bol-97",
    name: "Roberto Baggio",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 97,
    goals: 22,
    assists: 9,
    appearances: 30,
    starts: 27,
    tag: "Codino al Dall'Ara",
    note: "La stagione della rinascita: 22 gol, Mondiale riconquistato, Dall'Ara in piedi.",
    evidence: [evidenceRefs.bdfutbolBologna1997, evidenceRefs.transfermarktBologna1997],
  },
  {
    id: "kolyvanov-bol-97",
    name: "Igor Kolyvanov",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "seconda-punta", "trequartista"],
    country: "Russia",
    score: 88,
    goals: 9,
    assists: 4,
    appearances: 31,
    starts: 25,
    tag: "Sinistro russo",
    note: "Terzo violino offensivo, tecnico e cattivo nel momento giusto.",
    evidence: [evidenceRefs.bdfutbolBologna1997, evidenceRefs.transfermarktBologna1997],
  },
  {
    id: "fontolan-bol-97",
    name: "Davide Fontolan",
    club: "Bologna",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["seconda-punta", "trequartista"],
    country: "Italia",
    score: 84,
    goals: 2,
    appearances: 27,
    starts: 6,
    tag: "Cambio da VHS",
    note: "Carta cult per allungare la panchina narrativa del Bologna 97-98.",
    evidence: [evidenceRefs.bdfutbolBologna1997],
  },
  {
    id: "scirea-juv-82",
    name: "Gaetano Scirea",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 97,
    goals: 5,
    appearances: 30,
    starts: 30,
    tag: "Libero totale",
    note: "Libero elegante, trenta presenze e cinque gol nel ventesimo scudetto bianconero.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "cabrini-juv-82",
    name: "Antonio Cabrini",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 94,
    goals: 5,
    appearances: 29,
    starts: 29,
    tag: "Terzino campione",
    note: "Terzino mancino da ventinove presenze e cinque gol nella Juve della seconda stella.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "gentile-juv-81",
    name: "Claudio Gentile",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale", "braccetto"],
    country: "Italia",
    score: 91,
    goals: 2,
    appearances: 27,
    starts: 27,
    tag: "Marcatura mondiale",
    note: "Ventisette presenze e durezza tattica nella difesa che concede solo quattordici gol.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "brio-juv-81",
    name: "Sergio Brio",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Stopper operaio",
    note: "Fisico e marcature pesanti accanto a Scirea nel blocco della seconda stella.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "tardelli-juv-81",
    name: "Marco Tardelli",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 92,
    goals: 3,
    appearances: 22,
    starts: 22,
    tag: "Strappo bianconero",
    note: "Inserimenti, pressione e tre gol da mezzala nella corsa al titolo.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "bonini-juv-81",
    name: "Massimo Bonini",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "San Marino",
    score: 88,
    goals: 1,
    appearances: 28,
    starts: 13,
    tag: "Polmoni di Trap",
    note: "Tredici partenze, quindici ingressi e lavoro continuo nel centrocampo campione.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "furino-juv-81",
    name: "Giuseppe Furino",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 87,
    appearances: 27,
    starts: 27,
    tag: "Capitano di sostanza",
    note: "Ventisette presenze da schermo e governo emotivo nella Juve dello scudetto numero venti.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "brady-juv-81",
    name: "Liam Brady",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala", "trequartista"],
    country: "Irlanda",
    score: 93,
    goals: 5,
    appearances: 29,
    starts: 29,
    tag: "Sinistro irlandese",
    note: "Tecnica, rigori e cinque gol, incluso quello decisivo nell'ultima giornata.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "fanna-juv-81",
    name: "Pietro Fanna",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 21,
    starts: 7,
    tag: "Cambio largo",
    note: "Ala di rotazione con ventuno presenze complessive in Serie A per cambiare ritmo.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "marocchino-juv-81",
    name: "Domenico Marocchino",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["ATT", "CEN"],
    positionTags: ["ala", "seconda-punta", "trequartista"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Estro laterale",
    note: "Ventinove presenze da attaccante esterno nel tridente variabile di Trapattoni.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "virdis-juv-81",
    name: "Pietro Paolo Virdis",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 90,
    goals: 9,
    appearances: 30,
    starts: 30,
    tag: "Bomber interno",
    note: "Nove gol in trenta presenze: miglior marcatore bianconero nel campionato della seconda stella.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "galderisi-juv-81",
    name: "Giuseppe Galderisi",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 87,
    goals: 6,
    appearances: 16,
    starts: 11,
    tag: "Nanu decisivo",
    note: "Sedici presenze e sei gol da giovane punta nella rotazione scudetto.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "bettega-juv-81",
    name: "Roberto Bettega",
    club: "Juventus",
    season: "1981-82",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 5,
    appearances: 7,
    starts: 7,
    tag: "Classe ridotta",
    note: "Solo sette presenze di Serie A, ma cinque gol e qualita da icona bianconera.",
    evidence: [evidenceRefs.manualJuventus1981],
  },
  {
    id: "platini-juv-84",
    name: "Michel Platini",
    club: "Juventus",
    season: "1984-85",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "mezzala", "seconda-punta"],
    country: "Francia",
    score: 98,
    goals: 18,
    tag: "Le Roi",
    note: "Regia, gol e controllo del ritmo.",
  },
  {
    id: "baggio-juv-94",
    name: "Roberto Baggio",
    club: "Juventus",
    season: "1994-95",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 97,
    tag: "Divin Codino",
    note: "Il dieci che trasforma ogni scelta.",
  },
  {
    id: "cannavaro-juv-05",
    name: "Fabio Cannavaro",
    club: "Juventus",
    season: "2005-06",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 97,
    tag: "Marcatura d'oro",
    note: "Difensore da picco assoluto.",
  },
  {
    id: "delpiero-juv-97",
    name: "Alessandro Del Piero",
    club: "Juventus",
    season: "1997-98",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 97,
    goals: 21,
    tag: "Pinturicchio",
    note: "Gol a giro e leadership da leggenda.",
  },
  {
    id: "trezeguet-juv-01",
    name: "David Trezeguet",
    club: "Juventus",
    season: "2001-02",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Francia",
    score: 95,
    goals: 24,
    tag: "Re David",
    note: "Finalizzatore puro per chiudere le partite.",
  },
  {
    id: "chiellini-juv-16",
    name: "Giorgio Chiellini",
    club: "Juventus",
    season: "2016-17",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 96,
    tag: "BBC",
    note: "Aggressivita e copertura da reparto elite.",
  },
  {
    id: "bonucci-juv-16",
    name: "Leonardo Bonucci",
    club: "Juventus",
    season: "2016-17",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 94,
    tag: "Lancio lungo",
    note: "Difesa e costruzione nella stessa carta.",
  },
  {
    id: "baresi-mil-88",
    name: "Franco Baresi",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 98,
    goals: 2,
    appearances: 33,
    starts: 33,
    tag: "Capitano eterno",
    note: "La linea difensiva sale con lui.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "galli-mil-88",
    name: "Giovanni Galli",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 91,
    appearances: 32,
    starts: 32,
    tag: "Porta di Sacchi",
    note: "Trentadue presenze dietro la linea alta rossonera.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "tassotti-mil-88",
    name: "Mauro Tassotti",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 91,
    goals: 2,
    appearances: 30,
    starts: 30,
    tag: "Terzino feroce",
    note: "Spinta e marcatura per il lato destro del Milan europeo.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "maldini-mil-88",
    name: "Paolo Maldini",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 93,
    appearances: 26,
    starts: 26,
    tag: "Erede naturale",
    note: "Gia titolare affidabile nella difesa piu moderna del campionato.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "costacurta-mil-88",
    name: "Alessandro Costacurta",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 88,
    appearances: 26,
    starts: 20,
    tag: "Billy giovane",
    note: "Centrale gia pronto per assorbire la scuola di Sacchi.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "rijkaard-mil-88",
    name: "Frank Rijkaard",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "centrale"],
    country: "Paesi Bassi",
    score: 96,
    goals: 4,
    appearances: 31,
    starts: 31,
    tag: "Totale olandese",
    note: "Fisico, anticipo e uscita pulita tra difesa e mediana.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "ancelotti-mil-88",
    name: "Carlo Ancelotti",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 92,
    goals: 2,
    appearances: 28,
    starts: 27,
    tag: "Regia pragmatica",
    note: "Ordine e letture nella mediana che fa partire il pressing.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "evani-mil-88",
    name: "Alberico Evani",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["esterno", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 3,
    appearances: 30,
    starts: 29,
    tag: "Sinistra rossonera",
    note: "Piede mancino, ampiezza e equilibrio sul lato di Maldini.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "donadoni-mil-88",
    name: "Roberto Donadoni",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "mezzala"],
    country: "Italia",
    score: 92,
    goals: 1,
    appearances: 21,
    starts: 20,
    tag: "Strappi moderni",
    note: "Ala tecnica che trasforma campo aperto in vantaggio tattico.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "gullit-mil-88",
    name: "Ruud Gullit",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta", "mezzala"],
    country: "Paesi Bassi",
    score: 97,
    goals: 5,
    appearances: 19,
    starts: 17,
    tag: "Tulipano totale",
    note: "Potenza, tecnica e caos buono tra centrocampo e attacco.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "vanbasten-mil-88",
    name: "Marco van Basten",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Paesi Bassi",
    score: 98,
    goals: 19,
    appearances: 33,
    starts: 33,
    tag: "Cigno di Utrecht",
    note: "Diciannove gol e tecnica da centravanti totale.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "virdis-mil-88",
    name: "Pietro Paolo Virdis",
    club: "Milan",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 90,
    goals: 10,
    appearances: 26,
    starts: 17,
    tag: "Punta di mestiere",
    note: "Gol e movimenti da veterano accanto al talento olandese.",
    evidence: [evidenceRefs.bdfutbolMilan1988],
  },
  {
    id: "costacurta-mil-93",
    name: "Alessandro Costacurta",
    club: "Milan",
    season: "1993-94",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 93,
    tag: "Billy",
    note: "Solidita rossonera senza fronzoli.",
  },
  {
    id: "nesta-mil-03",
    name: "Alessandro Nesta",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 97,
    assists: 1,
    appearances: 26,
    starts: 26,
    tag: "Eleganza centrale",
    note: "Letture pulite nel blocco europeo.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "cafu-mil-03",
    name: "Cafu",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Brasile",
    score: 94,
    goals: 1,
    assists: 5,
    appearances: 28,
    starts: 26,
    tag: "Fascia destra",
    note: "Accelerazione brasiliana sul lato forte.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "seedorf-mil-03",
    name: "Clarence Seedorf",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Paesi Bassi",
    score: 93,
    goals: 3,
    assists: 6,
    appearances: 29,
    starts: 26,
    tag: "Mezzala di velluto",
    note: "Tecnica e fisico per legare Pirlo e Kakà.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "gattuso-mil-03",
    name: "Gennaro Gattuso",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 92,
    goals: 1,
    assists: 2,
    appearances: 33,
    starts: 29,
    tag: "Ringhio scudetto",
    note: "Pressing, recuperi e protezione dei fantasisti.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "tomasson-mil-03",
    name: "Jon Dahl Tomasson",
    club: "Milan",
    season: "2003-04",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Danimarca",
    score: 89,
    goals: 12,
    assists: 5,
    appearances: 26,
    starts: 17,
    tag: "Jolly scudetto",
    note: "Rotazione pesante: gol, assist e duttilita.",
    evidence: [evidenceRefs.statsCrewMilan2003, evidenceRefs.acMilanRoster2003],
  },
  {
    id: "seedorf-mil-06",
    name: "Clarence Seedorf",
    club: "Milan",
    season: "2006-07",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Paesi Bassi",
    score: 94,
    tag: "Musica europea",
    note: "Centrocampo tecnico e fisico insieme.",
  },
  {
    id: "gattuso-mil-06",
    name: "Gennaro Gattuso",
    club: "Milan",
    season: "2006-07",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 92,
    tag: "Ringhio",
    note: "Equilibrio feroce dietro ai fantasisti.",
  },
  {
    id: "inzaghi-mil-06",
    name: "Filippo Inzaghi",
    club: "Milan",
    season: "2006-07",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 93,
    goals: 11,
    tag: "Superpippo",
    note: "Sempre nel posto giusto nel momento giusto.",
  },
  {
    id: "bergomi-inter-88",
    name: "Giuseppe Bergomi",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 95,
    goals: 1,
    appearances: 32,
    starts: 32,
    tag: "Zio",
    note: "Difensore simbolo dell'Inter dei record.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "matthaus-inter-88",
    name: "Lothar Matthaus",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano", "regista"],
    country: "Germania",
    score: 97,
    goals: 9,
    appearances: 32,
    starts: 32,
    tag: "Tedesco totale",
    note: "Potenza e regia nel cuore del campo.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "brehme-inter-88",
    name: "Andreas Brehme",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Germania",
    score: 94,
    goals: 3,
    appearances: 31,
    starts: 31,
    tag: "Fascia sinistra",
    note: "Piede educato e copertura da scudetto.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "ferri-inter-88",
    name: "Riccardo Ferri",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 92,
    appearances: 31,
    starts: 31,
    tag: "Marcatura feroce",
    note: "Centrale ruvido nella difesa che concede pochissimo.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "mandorlini-inter-88",
    name: "Andrea Mandorlini",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["mediano", "centrale", "braccetto"],
    country: "Italia",
    score: 90,
    goals: 3,
    appearances: 26,
    starts: 26,
    tag: "Cerniera Trap",
    note: "Schermo e copertura per far correre le stelle nerazzurre.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "berti-inter-88",
    name: "Nicola Berti",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 92,
    goals: 7,
    appearances: 32,
    starts: 32,
    tag: "Mezzala verticale",
    note: "Strappi, inserimenti e sette gol nel motore dello scudetto.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "matteoli-inter-88",
    name: "Gianfranco Matteoli",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mediano", "mezzala"],
    country: "Italia",
    score: 89,
    goals: 2,
    appearances: 32,
    starts: 31,
    tag: "Ordine nerazzurro",
    note: "Cuce il centrocampo mentre Matthaus attacca e Mandorlini copre.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "bianchi-inter-88",
    name: "Alessandro Bianchi",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["esterno", "ala", "mezzala"],
    country: "Italia",
    score: 87,
    goals: 3,
    appearances: 31,
    starts: 26,
    tag: "Fascia operaia",
    note: "Ampiezza, rincorse e minuti pesanti nel sistema di Trapattoni.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "ramondiaz-inter-88",
    name: "Ramon Diaz",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Argentina",
    score: 91,
    goals: 12,
    appearances: 33,
    starts: 33,
    tag: "Spalla argentina",
    note: "Dodici gol e raccordo continuo accanto a Serena.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "serena-inter-88",
    name: "Aldo Serena",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 95,
    goals: 22,
    appearances: 32,
    starts: 32,
    tag: "Capocannoniere record",
    note: "Ventidue gol nella squadra che scappa via al campionato.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "terraneo-lec-88",
    name: "Giuliano Terraneo",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 88,
    appearances: 34,
    starts: 34,
    tag: "Porta salentina",
    note: "Titolare in tutte le giornate del Lecce nono, la migliore Serie A giallorossa.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "baroni-lec-88",
    name: "Marco Baroni",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    goals: 2,
    appearances: 33,
    starts: 33,
    tag: "Gol pesanti",
    note: "Difensore centrale con due gol, incluso quello che stende il Napoli al Via del Mare.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "vanoli-lec-88",
    name: "Rodolfo Vanoli",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 84,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Fascia di Mazzone",
    note: "Laterale da trenta presenze nella linea che tiene il Lecce a meta classifica.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "righetti-lec-88",
    name: "Ubaldo Righetti",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 27,
    starts: 27,
    tag: "Colonna d'esperienza",
    note: "Rinforzo difensivo indicato nel racconto della stagione, ventisette presenze vere.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "nobile-lec-88",
    name: "Salvatore Nobile",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 82,
    appearances: 26,
    starts: 24,
    tag: "Rientro utile",
    note: "Difensore rientrato in corso d'annata e subito dentro la rotazione principale.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "barbas-lec-88",
    name: "Juan Barbas",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Argentina",
    score: 90,
    goals: 3,
    appearances: 33,
    starts: 33,
    tag: "Capitano argentino",
    note: "Capitano e faro tecnico del miglior Lecce di Serie A.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "moriero-lec-88",
    name: "Francesco Moriero",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno"],
    country: "Italia",
    score: 87,
    goals: 2,
    appearances: 30,
    starts: 28,
    tag: "Ala del Via del Mare",
    note: "Diciannove anni, trenta presenze e il gol che apre la vittoria sulla Juventus.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "benedetti-lec-88",
    name: "Paolo Benedetti",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    goals: 4,
    appearances: 25,
    starts: 24,
    tag: "Innesto in mediana",
    note: "Quattro gol e gamba nel centrocampo citato come rinforzo chiave della stagione.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "enzo-lec-88",
    name: "Giorgio Enzo",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 83,
    appearances: 22,
    starts: 22,
    tag: "Ordine di reparto",
    note: "Centrocampista di continuita per tenere corta la squadra di Mazzone.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "pasculli-lec-88",
    name: "Pedro Pasculli",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 90,
    goals: 7,
    appearances: 30,
    starts: 29,
    tag: "Bomber mondiale",
    note: "Miglior marcatore in campionato, sette gol e nome chiave della salvezza alta.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "paciocco-lec-88",
    name: "Ricardo Paciocco",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 83,
    goals: 2,
    appearances: 26,
    starts: 22,
    tag: "Spalla di provincia",
    note: "Ventisei presenze e lavoro d'attacco nel reparto che ruota intorno a Pasculli.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "conte-lec-88",
    name: "Antonio Conte",
    club: "Lecce",
    season: "1988-89",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 82,
    appearances: 19,
    starts: 11,
    tag: "Conte prima di Conte",
    note: "Diciannove presenze da giovane centrocampista salentino prima della carriera bianconera.",
    evidence: [evidenceRefs.bdfutbolLecce1988, evidenceRefs.manualLecce1988],
  },
  {
    id: "materazzi-inter-06",
    name: "Marco Materazzi",
    club: "Inter",
    season: "2006-07",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 91,
    goals: 10,
    tag: "Matrix",
    note: "Carta ruvida, utile anche sui piazzati.",
  },
  {
    id: "samuel-inter-09",
    name: "Walter Samuel",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Argentina",
    score: 94,
    goals: 3,
    appearances: 28,
    starts: 25,
    tag: "Muro",
    note: "Il centrale che fa sparire gli attaccanti.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "cambiasso-inter-09",
    name: "Esteban Cambiasso",
    club: "Inter",
    season: "2009-10",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Argentina",
    score: 94,
    goals: 3,
    appearances: 30,
    starts: 26,
    tag: "Equilibrio triplete",
    note: "La scelta che tiene insieme il draft.",
    evidence: [evidenceRefs.worldFootballInter2009, evidenceRefs.interOfficial2009],
  },
  {
    id: "adriano-parma-02",
    name: "Adriano",
    club: "Parma",
    season: "2002-03",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Brasile",
    score: 92,
    tag: "Prima esplosione",
    note: "Sinistro pesante in versione gialloblu.",
  },
  {
    id: "koulibaly-nap-17",
    name: "Kalidou Koulibaly",
    club: "Napoli",
    season: "2017-18",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Senegal",
    score: 95,
    tag: "Muro azzurro",
    note: "Difesa alta e duelli vinti.",
  },
  {
    id: "ferrara-nap-86",
    name: "Ciro Ferrara",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 93,
    goals: 2,
    appearances: 28,
    starts: 26,
    tag: "Scudetto azzurro",
    note: "Centrale giovane, affidabile e storico.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "bruscolotti-nap-86",
    name: "Giuseppe Bruscolotti",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 91,
    appearances: 25,
    starts: 21,
    tag: "Capitano Pal e fierro",
    note: "Identita partenopea pura.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "ferrario-nap-86",
    name: "Moreno Ferrario",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 89,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Muro dello scudetto",
    note: "Presenza fissa nella difesa del primo Napoli campione.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "renica-nap-86",
    name: "Alessandro Renica",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 29,
    starts: 29,
    tag: "Difesa Ennerre",
    note: "Braccio affidabile della linea che regge il sogno azzurro.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "volpecina-nap-86",
    name: "Giuseppe Volpecina",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 25,
    starts: 15,
    tag: "Fascia azzurra",
    note: "Jolly di sinistra per completare il blocco Diego.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "bagni-nap-86",
    name: "Salvatore Bagni",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 90,
    goals: 4,
    appearances: 28,
    starts: 28,
    tag: "Motorino scudetto",
    note: "Pressing, inserimenti e carattere nel cuore del Napoli.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "denapoli-nap-86",
    name: "Fernando De Napoli",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 88,
    goals: 2,
    appearances: 28,
    starts: 28,
    tag: "Mediana azzurra",
    note: "Equilibrio e corsa per proteggere il numero dieci.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "romano-nap-86",
    name: "Francesco Romano",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Italia",
    score: 86,
    goals: 1,
    appearances: 22,
    starts: 22,
    tag: "Regia operaia",
    note: "Carta di sostanza per rendere credibile il centrocampo del primo scudetto.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "giordano-nap-86",
    name: "Bruno Giordano",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 90,
    goals: 5,
    appearances: 26,
    starts: 25,
    tag: "MA-GI senza CA",
    note: "Spalla tecnica di Diego prima dell'arrivo di Careca.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "carnevale-nap-86",
    name: "Andrea Carnevale",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 88,
    goals: 8,
    appearances: 27,
    starts: 18,
    tag: "Gol pesanti",
    note: "Centravanti di rotazione che nel primo scudetto lascia segni veri.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "caffarelli-nap-86",
    name: "Luigi Caffarelli",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["ATT", "CEN"],
    positionTags: ["seconda-punta", "trequartista", "ala"],
    country: "Italia",
    score: 85,
    goals: 3,
    appearances: 21,
    starts: 16,
    tag: "Rotazione da scudetto",
    note: "Scelta cult per dare profondita al Napoli di Bianchi.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "pecchia-nap-96",
    name: "Fabio Pecchia",
    club: "Napoli",
    season: "1996-97",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    tag: "Centrocampo di lotta",
    note: "Carta di raccordo per completare il cuore Napoli.",
  },
  {
    id: "careca-nap-87",
    name: "Careca",
    club: "Napoli",
    season: "1987-88",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Brasile",
    score: 95,
    goals: 13,
    tag: "Tridente MA-GI-CA",
    note: "Il partner nostalgico perfetto per Diego.",
  },
  {
    id: "cavani-nap-12",
    name: "Edinson Cavani",
    club: "Napoli",
    season: "2012-13",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Uruguay",
    score: 95,
    goals: 29,
    tag: "Matador",
    note: "Pressing e gol da stagione totale.",
  },
  {
    id: "alisson-roma-17",
    name: "Alisson",
    club: "Roma",
    season: "2017-18",
    era: "anni2010",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Brasile",
    score: 94,
    tag: "Porta europea",
    note: "Reattivita moderna per blindare il draft.",
  },
  {
    id: "aldair-roma-00",
    name: "Aldair",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Brasile",
    score: 94,
    appearances: 15,
    starts: 14,
    tag: "Pluto",
    note: "Leadership silenziosa nella Roma scudetto.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "candela-roma-00",
    name: "Vincent Candela",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Francia",
    score: 91,
    goals: 3,
    appearances: 33,
    starts: 33,
    tag: "Sinistra fluida",
    note: "Ampiezza e tecnica in fascia.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "samuel-roma-00",
    name: "Walter Samuel",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Argentina",
    score: 93,
    goals: 1,
    appearances: 31,
    starts: 31,
    tag: "Muro argentino",
    note: "Duelli sporchi vinti con calma.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "tommasi-roma-00",
    name: "Damiano Tommasi",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 90,
    goals: 3,
    appearances: 34,
    starts: 31,
    tag: "Anima operaia",
    note: "Il mediano che tiene in piedi il tridente scudetto.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "delvecchio-roma-00",
    name: "Marco Delvecchio",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 3,
    appearances: 31,
    starts: 28,
    tag: "Derby e sportellate",
    note: "Carta di sacrificio per non rompere il blocco romanista.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "zago-roma-00",
    name: "Antonio Carlos Zago",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Brasile",
    score: 88,
    appearances: 28,
    starts: 28,
    tag: "Ruvido da scudetto",
    note: "Centrale sporco e utile quando serve chiudere il draft.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "emerson-roma-00",
    name: "Emerson",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mediano", "regista"],
    country: "Brasile",
    score: 90,
    goals: 3,
    appearances: 13,
    starts: 10,
    tag: "Puma",
    note: "Regia fisica in formato cameo pesante.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "derossi-roma-06",
    name: "Daniele De Rossi",
    club: "Roma",
    season: "2006-07",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 94,
    tag: "Capitan futuro",
    note: "Schermo, tiro e romanismo nel mezzo.",
  },
  {
    id: "nainggolan-roma-16",
    name: "Radja Nainggolan",
    club: "Roma",
    season: "2016-17",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto"],
    country: "Belgio",
    score: 92,
    tag: "Ninja",
    note: "Recuperi e strappi per alzare l'intensita.",
  },
  {
    id: "montella-roma-00",
    name: "Vincenzo Montella",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 13,
    appearances: 28,
    starts: 12,
    tag: "Aeroplanino",
    note: "La terza punta che completa l'easter egg Roma.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "cassano-roma-03",
    name: "Antonio Cassano",
    club: "Roma",
    season: "2003-04",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 14,
    tag: "Fantantonio",
    note: "Dribbling, improvvisazione e caos creativo.",
  },
  {
    id: "marchegiani-laz-99",
    name: "Luca Marchegiani",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 91,
    appearances: 28,
    tag: "Portiere scudetto",
    note: "Ventotto presenze in Serie A e porta da titolo nel blocco Eriksson.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "mihajlovic-laz-99",
    name: "Sinisa Mihajlovic",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Serbia",
    score: 92,
    goals: 6,
    appearances: 26,
    tag: "Punizioni e lanci",
    note: "Centrale mancino con piede da trequartista.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "pancaro-laz-99",
    name: "Giuseppe Pancaro",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 89,
    goals: 3,
    appearances: 28,
    tag: "Fascia da scudetto",
    note: "Minuti pesanti, tre gol e spinta mancina nella Lazio del titolo.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "conceicao-laz-99",
    name: "Sergio Conceicao",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Portogallo",
    score: 90,
    goals: 2,
    appearances: 30,
    tag: "Strappi portoghesi",
    note: "Trenta presenze in Serie A, corsia aggressiva e due gol nella volata scudetto.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "sinzaghi-laz-99",
    name: "Simone Inzaghi",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 7,
    appearances: 22,
    tag: "Punta di coppa",
    note: "Sette gol in campionato e notti europee sempre vive nel repertorio.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "stam-laz-01",
    name: "Jaap Stam",
    club: "Lazio",
    season: "2001-02",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Paesi Bassi",
    score: 93,
    tag: "Fisico totale",
    note: "Marcatura pesante per partite bloccate.",
  },
  {
    id: "favalli-laz-99",
    name: "Giuseppe Favalli",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 89,
    appearances: 18,
    tag: "Fascia scudetto",
    note: "Scelta utile per completare la linea.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "negro-laz-99",
    name: "Paolo Negro",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 89,
    goals: 2,
    appearances: 26,
    tag: "Difesa biancoceleste",
    note: "Affidabile e coerente col blocco titolo.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "milinkovic-laz-17",
    name: "Sergej Milinkovic-Savic",
    club: "Lazio",
    season: "2017-18",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Serbia",
    score: 93,
    goals: 12,
    tag: "Sergente",
    note: "Fisico, tecnica e inserimenti.",
  },
  {
    id: "simeone-laz-99",
    name: "Diego Simeone",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Argentina",
    score: 92,
    goals: 5,
    appearances: 28,
    tag: "Garrrrrra",
    note: "Centrocampo feroce da scudetto.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "orsi-laz-92",
    name: "Fernando Orsi",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 85,
    appearances: 23,
    starts: 23,
    tag: "Porta di Zoff",
    note: "Primo portiere della Lazio quinta, nella prima stagione piena dell'era Cragnotti.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "fiori-laz-92",
    name: "Valerio Fiori",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 81,
    appearances: 11,
    starts: 11,
    tag: "Staffetta in porta",
    note: "Undici presenze vere per dare profondita anche ai guanti biancocelesti.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "favalli-laz-92",
    name: "Giuseppe Favalli",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 87,
    goals: 1,
    appearances: 32,
    starts: 31,
    tag: "Fascia sinistra",
    note: "Trentadue presenze e corsia continua nel lato sinistro della Lazio di Zoff.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "bacci-laz-92",
    name: "Roberto Bacci",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 83,
    appearances: 31,
    starts: 28,
    tag: "Difensore di volume",
    note: "Ventotto partenze e continuita nella retroguardia che accompagna la rinascita laziale.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "cravero-laz-92",
    name: "Roberto Cravero",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 86,
    goals: 3,
    appearances: 30,
    starts: 29,
    tag: "Libero esperto",
    note: "Tre gol e leadership da centrale nella Lazio che torna stabilmente in alto.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "luzardi-laz-92",
    name: "Maurizio Luzardi",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 83,
    goals: 1,
    appearances: 25,
    starts: 24,
    tag: "Linea solida",
    note: "Venticinque presenze e un gol dentro una difesa piena di minutaggio italiano.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "bergodi-laz-92",
    name: "Cristiano Bergodi",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 82,
    appearances: 16,
    starts: 13,
    tag: "Rotazione difensiva",
    note: "Tredici partenze per rendere piu larga la scelta difensiva del blocco Olimpico.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "fuser-laz-92",
    name: "Diego Fuser",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 10,
    appearances: 33,
    starts: 33,
    tag: "Dieci gol larghi",
    note: "Trentatre presenze e dieci reti: ampiezza, corsa e tiro nella Lazio pre-scudetto.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "winter-laz-92",
    name: "Aron Winter",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Paesi Bassi",
    score: 89,
    goals: 6,
    appearances: 30,
    starts: 30,
    tag: "Ajax all'Olimpico",
    note: "Sei gol e struttura europea al centro del campo nella nuova Lazio internazionale.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "gascoigne-laz-92",
    name: "Paul Gascoigne",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Inghilterra",
    score: 89,
    goals: 4,
    appearances: 22,
    starts: 22,
    tag: "Gazza romano",
    note: "Quattro gol, talento fragile e un derby gia leggendario nella memoria biancoceleste.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "doll-laz-92",
    name: "Thomas Doll",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["trequartista", "mezzala"],
    country: "Germania",
    score: 86,
    goals: 2,
    appearances: 20,
    starts: 19,
    tag: "Tecnica tedesca",
    note: "Qualita tra le linee e diciannove partenze in una mediana finalmente europea.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "sclosa-laz-92",
    name: "Claudio Sclosa",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 81,
    appearances: 21,
    starts: 10,
    tag: "Minuti di copertura",
    note: "Rotazione di centrocampo per tenere vivo il pacchetto di scelte nostalgia.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "signori-laz-92",
    name: "Giuseppe Signori",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 94,
    goals: 26,
    appearances: 32,
    tag: "Beppe-gol",
    note: "Finalizzazione pura e nostalgia Olimpico.",
    evidence: [evidenceRefs.signoriRsssf, evidenceRefs.manualLazio1992],
  },
  {
    id: "riedle-laz-92",
    name: "Karl-Heinz Riedle",
    club: "Lazio",
    season: "1992-93",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Germania",
    score: 88,
    goals: 8,
    appearances: 22,
    starts: 21,
    tag: "Spalla mondiale",
    note: "Otto gol accanto a Signori, prima di diventare specialista delle notti europee.",
    evidence: [evidenceRefs.manualLazio1992],
  },
  {
    id: "salas-laz-99",
    name: "Marcelo Salas",
    club: "Lazio",
    season: "1999-00",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Cile",
    score: 92,
    goals: 12,
    appearances: 28,
    tag: "Matador cileno",
    note: "Attacco verticale nel blocco titolo.",
    evidence: [evidenceRefs.lazioStories1999],
  },
  {
    id: "klose-laz-11",
    name: "Miroslav Klose",
    club: "Lazio",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Germania",
    score: 91,
    goals: 13,
    assists: 5,
    appearances: 27,
    tag: "Senso del gol",
    note: "Carta intelligente per rotazioni offensive.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "cannavaro-parma-98",
    name: "Fabio Cannavaro",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 94,
    goals: 1,
    appearances: 30,
    starts: 30,
    tag: "Parma europeo",
    note: "Uno dei pilastri del nucleo gialloblu.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "benarrivo-parma-98",
    name: "Antonio Benarrivo",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 90,
    assists: 1,
    appearances: 25,
    starts: 25,
    tag: "Terzino coppa",
    note: "Ampiezza storica del Parma anni 90.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "sensini-parma-98",
    name: "Roberto Sensini",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["mediano", "centrale", "braccetto"],
    country: "Argentina",
    score: 91,
    goals: 1,
    assists: 1,
    appearances: 26,
    starts: 25,
    tag: "Esperienza",
    note: "Carta flessibile per difesa o mediana.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "dinobaggio-parma-98",
    name: "Dino Baggio",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 91,
    goals: 2,
    assists: 3,
    appearances: 29,
    starts: 29,
    tag: "Inserimento",
    note: "Centrocampista da notti di coppa.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "veron-parma-98",
    name: "Juan Sebastian Veron",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Argentina",
    score: 93,
    goals: 1,
    assists: 8,
    appearances: 26,
    starts: 26,
    tag: "Prima regia",
    note: "Versione Parma della Brujita.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "fuser-parma-98",
    name: "Diego Fuser",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto", "ala"],
    country: "Italia",
    score: 90,
    goals: 8,
    assists: 7,
    appearances: 32,
    starts: 31,
    tag: "Fascia e piazzati",
    note: "Esterno da cross, punizioni e assist: Provincia vera.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "stanic-parma-98",
    name: "Mario Stanic",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Croazia",
    score: 87,
    goals: 7,
    appearances: 18,
    starts: 7,
    tag: "Jolly croato",
    note: "Carta da rotazione con gol pesanti e ruolo elastico.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "fiore-parma-98",
    name: "Stefano Fiore",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 16,
    tag: "Talento in mediana",
    note: "Profondita tecnica per non pescare sempre gli stessi nomi.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "zola-parma-94",
    name: "Gianfranco Zola",
    club: "Parma",
    season: "1994-95",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 93,
    tag: "Magia piccola",
    note: "Tecnica stretta e punizioni.",
  },
  {
    id: "chiesa-parma-98",
    name: "Enrico Chiesa",
    club: "Parma",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 92,
    goals: 9,
    assists: 10,
    appearances: 30,
    starts: 29,
    tag: "Gol di coppa",
    note: "Seconda punta perfetta accanto a Crespo.",
    evidence: [evidenceRefs.statsCrewParma1998],
  },
  {
    id: "pagliuca-sam-90",
    name: "Gianluca Pagliuca",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 93,
    appearances: 32,
    starts: 32,
    tag: "Porta scudetto",
    note: "Riflessi e identita doriana.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "vierchowod-sam-90",
    name: "Pietro Vierchowod",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 94,
    goals: 3,
    appearances: 30,
    starts: 30,
    tag: "Zar",
    note: "Marcature durissime, zero sconti.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "mannini-sam-90",
    name: "Moreno Mannini",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 90,
    goals: 2,
    appearances: 26,
    starts: 26,
    tag: "Fascia blucerchiata",
    note: "Terzino da scudetto pragmatico.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "lanna-sam-90",
    name: "Marco Lanna",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 89,
    appearances: 26,
    starts: 20,
    tag: "Linea scudetto",
    note: "Difensore affidabile per chiudere la XI.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "katanec-sam-90",
    name: "Srecko Katanec",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "braccetto", "mediano"],
    country: "Slovenia",
    score: 90,
    goals: 2,
    appearances: 26,
    starts: 26,
    tag: "Jolly storico",
    note: "Copre due reparti senza rompere l'intesa.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "cerezo-sam-90",
    name: "Toninho Cerezo",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Brasile",
    score: 92,
    goals: 3,
    appearances: 12,
    starts: 11,
    tag: "Regia brasiliana",
    note: "Calma e idee nel centrocampo blucerchiato.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "lombardo-sam-90",
    name: "Attilio Lombardo",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 3,
    appearances: 32,
    starts: 32,
    tag: "Popeye",
    note: "Corsa e ampiezza per cambiare ritmo.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "branca-sam-90",
    name: "Marco Branca",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 86,
    goals: 5,
    appearances: 20,
    starts: 8,
    tag: "Cambio pesante",
    note: "Punta di rotazione che mantiene vivo il blocco scudetto.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "dossena-sam-90",
    name: "Giuseppe Dossena",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 34,
    starts: 34,
    tag: "Equilibrio blucerchiato",
    note: "Sempre presente nella macchina di Boskov.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "pari-sam-90",
    name: "Fausto Pari",
    club: "Sampdoria",
    season: "1990-91",
    era: "anni90",
    roles: ["CEN", "DIF"],
    positionTags: ["mediano", "centrale", "braccetto"],
    country: "Italia",
    score: 88,
    appearances: 33,
    starts: 32,
    tag: "Cerniera",
    note: "Carta elastica per tenere compatto il centrocampo.",
    evidence: [evidenceRefs.statsCrewSamp1990],
  },
  {
    id: "montella-sam-96",
    name: "Vincenzo Montella",
    club: "Sampdoria",
    season: "1996-97",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 22,
    tag: "Aeroplanino blucerchiato",
    note: "Versione Samp da gol in raffica.",
  },
  {
    id: "quagliarella-sam-18",
    name: "Fabio Quagliarella",
    club: "Sampdoria",
    season: "2018-19",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 92,
    goals: 26,
    tag: "Gol impossibili",
    note: "Tacco, volo, giro: una carta cult moderna.",
  },
  {
    id: "toldo-fio-98",
    name: "Francesco Toldo",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 93,
    appearances: 33,
    starts: 33,
    tag: "Guanti viola",
    note: "Portiere da grande provincia e notti pesanti.",
    evidence: [evidenceRefs.statsCrewFiorentina1998, evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "torricelli-fio-98",
    name: "Moreno Torricelli",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 87,
    goals: 2,
    appearances: 31,
    starts: 31,
    tag: "Terzino operaio",
    note: "Esperienza e gamba nella Fiorentina che guarda lo scudetto.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "heinrich-fio-98",
    name: "Jorg Heinrich",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Germania",
    score: 88,
    goals: 3,
    appearances: 33,
    starts: 33,
    tag: "Fascia tedesca",
    note: "Laterale totale per dare ampiezza e sostanza al blocco viola.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "repka-fio-98",
    name: "Tomas Repka",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Repubblica Ceca",
    score: 88,
    appearances: 31,
    starts: 31,
    tag: "Marcatura ruvida",
    note: "Difensore duro da Serie A anni 90, perfetto per una carta Provincia.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "padalino-fio-98",
    name: "Pasquale Padalino",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 87,
    goals: 3,
    appearances: 28,
    starts: 28,
    tag: "Difesa viola",
    note: "Presenza solida nella linea del Franchi pieno.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "ruicosta-fio-98",
    name: "Rui Costa",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Portogallo",
    score: 94,
    goals: 10,
    appearances: 31,
    starts: 31,
    tag: "Maestro viola",
    note: "Luce tecnica dietro Batigol, da pescare come creatore di lusso.",
    evidence: [evidenceRefs.statsCrewFiorentina1998, evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "amoroso-fio-98",
    name: "Christian Amoroso",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 86,
    appearances: 28,
    starts: 28,
    tag: "Mediana viola",
    note: "Equilibrio e copertura per lasciare campo ai fantasisti.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "cois-fio-98",
    name: "Sandro Cois",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    appearances: 23,
    starts: 22,
    tag: "Polmoni al Franchi",
    note: "Carta da lavoro sporco per sostenere il trio offensivo.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "edmundo-fio-98",
    name: "Edmundo",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Brasile",
    score: 91,
    goals: 8,
    appearances: 28,
    starts: 28,
    tag: "Carnevale viola",
    note: "Caos brasiliano, genio e folklore nel sogno incompiuto.",
    evidence: [evidenceRefs.statsCrewFiorentina1998, evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "oliveira-fio-98",
    name: "Luis Oliveira",
    club: "Fiorentina",
    season: "1998-99",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Belgio",
    score: 87,
    goals: 2,
    appearances: 30,
    starts: 27,
    tag: "Profondita viola",
    note: "Attaccante di raccordo per completare un tridente anni 90.",
    evidence: [evidenceRefs.bdfutbolFiorentina1998],
  },
  {
    id: "frey-parma-01",
    name: "Sebastien Frey",
    club: "Parma",
    season: "2001-02",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Francia",
    score: 90,
    tag: "Riflessi gialloblu",
    note: "Scelta da cult hero per completare il blocco Parma.",
  },
  {
    id: "handanovic-udi-10",
    name: "Samir Handanovic",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Slovenia",
    score: 91,
    appearances: 35,
    starts: 35,
    tag: "Para-rigori",
    note: "Udinese europea, rigori letti in anticipo.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "zapata-udi-10",
    name: "Cristian Zapata",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Colombia",
    score: 87,
    goals: 2,
    appearances: 35,
    starts: 35,
    tag: "Muro friulano",
    note: "Presenza centrale nella linea che porta l'Udinese in Champions.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "benatia-udi-10",
    name: "Mehdi Benatia",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Marocco",
    score: 88,
    goals: 3,
    appearances: 34,
    starts: 34,
    tag: "Scouting da Champions",
    note: "Difensore moderno pescato dal laboratorio friulano.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "domizzi-udi-10",
    name: "Maurizio Domizzi",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["DIF"],
    positionTags: ["braccetto", "centrale"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 30,
    starts: 28,
    tag: "Braccetto mancino",
    note: "Esperienza e sinistro per tenere alta la difesa a tre.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "isla-udi-10",
    name: "Mauricio Isla",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Cile",
    score: 87,
    goals: 2,
    appearances: 34,
    starts: 34,
    tag: "Tutta fascia",
    note: "Corsa cilena per collegare difesa, mediana e strappi di Sanchez.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "armero-udi-10",
    name: "Pablo Armero",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Colombia",
    score: 86,
    goals: 2,
    appearances: 31,
    starts: 29,
    tag: "Fascia infinita",
    note: "Accelerazioni da Friuli aperto, perfetto per Provincia Magica.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "asamoah-udi-10",
    name: "Kwadwo Asamoah",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Ghana",
    score: 88,
    goals: 2,
    appearances: 38,
    starts: 37,
    tag: "Motore friulano",
    note: "Fisico, conduzione e presenza continua nel centrocampo di Guidolin.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "inler-udi-10",
    name: "Gokhan Inler",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["regista", "mediano"],
    country: "Svizzera",
    score: 87,
    goals: 3,
    appearances: 35,
    starts: 35,
    tag: "Regia e botta",
    note: "Equilibratore con tiro da fuori nel cuore dell'Udinese europea.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "pinzi-udi-10",
    name: "Giampiero Pinzi",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    goals: 1,
    appearances: 34,
    starts: 30,
    tag: "Pressing friulano",
    note: "Carta da equilibrio sporco per far funzionare il blocco Udinese.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "sanchez-udi-10",
    name: "Alexis Sanchez",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Cile",
    score: 93,
    goals: 12,
    appearances: 31,
    starts: 27,
    tag: "Nino maravilla",
    note: "La scintilla che rende l'Udinese di Guidolin un cheat code da provincia.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "denis-udi-10",
    name: "German Denis",
    club: "Udinese",
    season: "2010-11",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 84,
    goals: 4,
    appearances: 25,
    starts: 8,
    tag: "Tanque di rotazione",
    note: "Prima del culto atalantino, una carta utile nel laboratorio Udinese.",
    evidence: [evidenceRefs.statsCrewUdinese2010, evidenceRefs.bdfutbolUdinese2010],
  },
  {
    id: "sirigu-pal-09",
    name: "Salvatore Sirigu",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 89,
    appearances: 32,
    starts: 32,
    tag: "Barbera sicuro",
    note: "Portiere giovane nel Palermo che sfiora la Champions.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "cassani-pal-09",
    name: "Mattia Cassani",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 86,
    appearances: 37,
    starts: 37,
    tag: "Fascia rosanero",
    note: "Terzino costante nel Palermo piu europeo di Zamparini.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "balzaretti-pal-09",
    name: "Federico Balzaretti",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 87,
    goals: 1,
    appearances: 34,
    starts: 34,
    tag: "Sinistra del Barbera",
    note: "Spinta e affidabilita nella catena rosanero.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "kjaer-pal-09",
    name: "Simon Kjaer",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Danimarca",
    score: 88,
    goals: 2,
    appearances: 35,
    starts: 35,
    tag: "Scouting rosanero",
    note: "Centrale giovane pescato dal mercato giusto prima del grande salto.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "bovo-pal-09",
    name: "Cesare Bovo",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 29,
    starts: 29,
    tag: "Muro e piazzati",
    note: "Difensore di sostanza con piede pesante sui calci fermi.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "nocerino-pal-09",
    name: "Antonio Nocerino",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["esterno", "quinto"],
    country: "Italia",
    score: 86,
    goals: 2,
    appearances: 35,
    starts: 34,
    tag: "Corsa rosanero",
    note: "Pressing, strappi e chilometri per tenere in piedi i fantasisti.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "migliaccio-pal-09",
    name: "Giulio Migliaccio",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Italia",
    score: 85,
    appearances: 33,
    starts: 31,
    tag: "Mediana ruvida",
    note: "Carta di equilibrio per far respirare Pastore e Miccoli.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "simplicio-pal-09",
    name: "Fabio Simplicio",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["CEN", "ATT"],
    positionTags: ["mezzala", "trequartista", "seconda-punta"],
    country: "Brasile",
    score: 88,
    goals: 3,
    appearances: 28,
    starts: 27,
    tag: "Inserimenti brasiliani",
    note: "Centrocampista offensivo da raccordo nel rombo rosanero.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "miccoli-pal-09",
    name: "Fabrizio Miccoli",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 93,
    goals: 19,
    appearances: 35,
    starts: 32,
    tag: "Romario del Salento",
    note: "Capitano, punizioni e Barbera acceso fino alla corsa Champions.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "cavani-pal-09",
    name: "Edinson Cavani",
    club: "Palermo",
    season: "2009-10",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Uruguay",
    score: 91,
    goals: 13,
    appearances: 34,
    starts: 31,
    tag: "Matador rosanero",
    note: "Il Cavani prima di Napoli, gia pronto a spaccare le partite.",
    evidence: [evidenceRefs.bdfutbolPalermo2009],
  },
  {
    id: "miccoli-pal-11",
    name: "Fabrizio Miccoli",
    club: "Palermo",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 92,
    goals: 16,
    assists: 12,
    appearances: 28,
    tag: "Romario del Salento",
    note: "Gol e assist al Barbera: carta Provincia con fantasia pura.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "giovinco-parma-11",
    name: "Sebastian Giovinco",
    club: "Parma",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Italia",
    score: 91,
    goals: 15,
    assists: 11,
    appearances: 36,
    tag: "Formica atomica",
    note: "Piccolo dieci da draft: accelera intesa e creativita.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "denis-ata-11",
    name: "German Denis",
    club: "Atalanta",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Argentina",
    score: 90,
    goals: 16,
    appearances: 33,
    tag: "Tanque",
    note: "Centravanti da provincia ruvida: pochi fronzoli, tanti gol.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "jovetic-fio-11",
    name: "Stevan Jovetic",
    club: "Fiorentina",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT", "CEN"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Montenegro",
    score: 91,
    goals: 14,
    appearances: 27,
    tag: "Jo-Jo viola",
    note: "Trequarti elegante per mischiare finalizzazione e intesa.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "divaio-bol-11",
    name: "Marco Di Vaio",
    club: "Bologna",
    season: "2011-12",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 89,
    goals: 10,
    assists: 6,
    appearances: 37,
    tag: "Capitano rossoblu",
    note: "Bomber di continuita, perfetto quando il draft chiede sostanza.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "diamanti-bol-11",
    name: "Alessandro Diamanti",
    club: "Bologna",
    season: "2011-12",
    era: "anni2010",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta", "ala"],
    country: "Italia",
    score: 89,
    goals: 7,
    assists: 6,
    appearances: 30,
    tag: "Alino mancino",
    note: "Punizioni, strappi e traiettorie strane da provincia romantica.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "moralez-ata-11",
    name: "Maxi Moralez",
    club: "Atalanta",
    season: "2011-12",
    era: "anni2010",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Argentina",
    score: 88,
    goals: 6,
    assists: 5,
    appearances: 34,
    tag: "Frasquito",
    note: "Carta agile per legare Atalanta, Argentina e linee offensive.",
    evidence: [evidenceRefs.espnSerieA2011],
  },
  {
    id: "zenga-inter-88",
    name: "Walter Zenga",
    club: "Inter",
    season: "1988-89",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 94,
    appearances: 33,
    starts: 33,
    tag: "Uomo Ragno",
    note: "Portiere simbolo dell'Inter dei record.",
    evidence: [evidenceRefs.bdfutbolInter1988],
  },
  {
    id: "garella-nap-86",
    name: "Claudio Garella",
    club: "Napoli",
    season: "1986-87",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 89,
    appearances: 29,
    starts: 29,
    tag: "Scudetto azzurro",
    note: "Il portiere del primo scudetto del Napoli.",
    evidence: [evidenceRefs.statsCrewNapoli1986],
  },
  {
    id: "antonioli-roma-00",
    name: "Francesco Antonioli",
    club: "Roma",
    season: "2000-01",
    era: "anni2000",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 88,
    appearances: 26,
    starts: 26,
    tag: "Porta scudetto",
    note: "Equilibrio e affidabilita nella Roma campione.",
    evidence: [evidenceRefs.worldFootballRoma2000],
  },
  {
    id: "audero-sam-20",
    name: "Emil Audero",
    club: "Sampdoria",
    season: "2020-21",
    era: "moderna",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 84,
    tag: "Porta moderna",
    note: "Profondita moderna per la storia blucerchiata.",
  },
  {
    id: "falcao-roma-82",
    name: "Paulo Roberto Falcao",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Brasile",
    score: 96,
    goals: 7,
    appearances: 27,
    starts: 27,
    tag: "Ottavo re",
    note: "Il cervello brasiliano dello scudetto romanista anni 80.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "tancredi-roma-82",
    name: "Franco Tancredi",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["POR"],
    positionTags: ["portiere"],
    country: "Italia",
    score: 91,
    appearances: 30,
    starts: 30,
    tag: "Porta giallorossa",
    note: "Tutte le partite di Serie A nello scudetto di Liedholm, con una difesa da titolo davanti.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "nela-roma-82",
    name: "Sebastiano Nela",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "centrale"],
    country: "Italia",
    score: 89,
    goals: 2,
    appearances: 28,
    starts: 28,
    tag: "Fascia scudetto",
    note: "Terzino mancino, duelli e due gol nella Roma che torna campione.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "vierchowod-roma-82",
    name: "Pietro Vierchowod",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 92,
    appearances: 30,
    starts: 30,
    tag: "Stopper dello scudetto",
    note: "Fisico, anticipo e trenta presenze nella linea che regge il titolo.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "dibartolomei-roma-82",
    name: "Agostino Di Bartolomei",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["centrale", "mediano"],
    country: "Italia",
    score: 94,
    goals: 7,
    appearances: 28,
    starts: 28,
    tag: "Capitano regista",
    note: "Piede, rigori e comando basso nella Roma campione d'Italia.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "maldera-roma-82",
    name: "Aldo Maldera",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF", "CEN"],
    positionTags: ["terzino", "quinto", "esterno"],
    country: "Italia",
    score: 88,
    goals: 1,
    appearances: 26,
    starts: 26,
    tag: "Sinistra esperta",
    note: "Spinta e mestiere sulla corsia della squadra di Liedholm.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "righetti-roma-82",
    name: "Ubaldo Righetti",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Italia",
    score: 85,
    appearances: 12,
    starts: 12,
    tag: "Cambio difensivo",
    note: "Minuti veri da centrale giovane per dare profondita alla difesa scudetto.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "nappi-roma-82",
    name: "Michele Nappi",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["DIF"],
    positionTags: ["terzino", "centrale"],
    country: "Italia",
    score: 84,
    appearances: 16,
    starts: 10,
    tag: "Jolly difensivo",
    note: "Duttilita da retroguardia per completare il blocco romanista.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "ancelotti-roma-82",
    name: "Carlo Ancelotti",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 90,
    goals: 2,
    appearances: 23,
    starts: 22,
    tag: "Equilibrio Liedholm",
    note: "Centrocampo, tempi e due gol nella stagione del secondo scudetto romanista.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "prohaska-roma-82",
    name: "Herbert Prohaska",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["regista", "mezzala"],
    country: "Austria",
    score: 90,
    goals: 3,
    appearances: 26,
    starts: 26,
    tag: "Tecnica viennese",
    note: "Qualita e continuita nel centrocampo che accompagna Falcao.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "conti-roma-82",
    name: "Bruno Conti",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 93,
    goals: 3,
    appearances: 26,
    starts: 26,
    tag: "Ala mondiale",
    note: "Dribbling mancino e tre gol da esterno creativo nello scudetto giallorosso.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "valigi-roma-82",
    name: "Claudio Valigi",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN"],
    positionTags: ["mediano", "mezzala"],
    country: "Italia",
    score: 84,
    appearances: 13,
    starts: 11,
    tag: "Rotazione di sostanza",
    note: "Tredici presenze di campionato per allungare il centrocampo dello scudetto.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "chierico-roma-82",
    name: "Odoacre Chierico",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["CEN", "ATT"],
    positionTags: ["ala", "esterno", "trequartista"],
    country: "Italia",
    score: 85,
    goals: 2,
    appearances: 16,
    starts: 8,
    tag: "Cambio creativo",
    note: "Strappi, coppa e due gol in Serie A per dare opzioni alle corsie.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "pruzzo-roma-82",
    name: "Roberto Pruzzo",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["centravanti"],
    country: "Italia",
    score: 95,
    goals: 12,
    appearances: 27,
    starts: 27,
    tag: "Bomber scudetto",
    note: "Dodici gol in campionato e capocannoniere interno della Roma campione.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "iorio-roma-82",
    name: "Maurizio Iorio",
    club: "Roma",
    season: "1982-83",
    era: "anni80",
    roles: ["ATT"],
    positionTags: ["seconda-punta", "centravanti"],
    country: "Italia",
    score: 89,
    goals: 5,
    appearances: 25,
    starts: 22,
    tag: "Spalla giallorossa",
    note: "Movimento e cinque gol accanto a Pruzzo nella corsa al titolo.",
    evidence: [evidenceRefs.manualRoma1982],
  },
  {
    id: "panucci-roma-06",
    name: "Christian Panucci",
    club: "Roma",
    season: "2006-07",
    era: "anni2000",
    roles: ["DIF"],
    positionTags: ["terzino", "quinto"],
    country: "Italia",
    score: 90,
    tag: "Terzino esperto",
    note: "Leadership, diagonali e gol pesanti sul secondo palo.",
  },
  {
    id: "dzeko-roma-16",
    name: "Edin Dzeko",
    club: "Roma",
    season: "2016-17",
    era: "anni2010",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Bosnia ed Erzegovina",
    score: 94,
    goals: 29,
    appearances: 37,
    tag: "Capocannoniere",
    note: "Stagione da nove dominante, con 29 gol in Serie A.",
  },
  {
    id: "asprilla-parma-93",
    name: "Faustino Asprilla",
    club: "Parma",
    season: "1993-94",
    era: "anni90",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Colombia",
    score: 92,
    tag: "Tino",
    note: "Talento imprevedibile per dare caos buono al draft Parma.",
  },
  {
    id: "gilardino-parma-04",
    name: "Alberto Gilardino",
    club: "Parma",
    season: "2004-05",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 94,
    goals: 24,
    appearances: 39,
    tag: "Gila",
    note: "Finalizzatore totale nella stagione della salvezza allo spareggio.",
  },
  {
    id: "brolin-parma-93",
    name: "Tomas Brolin",
    club: "Parma",
    season: "1993-94",
    era: "anni90",
    roles: ["CEN", "ATT"],
    positionTags: ["trequartista", "seconda-punta"],
    country: "Svezia",
    score: 91,
    tag: "Jolly svedese",
    note: "Tecnica e inserimenti per un Parma piu vario.",
  },
  {
    id: "mihajlovic-sam-94",
    name: "Sinisa Mihajlovic",
    club: "Sampdoria",
    season: "1994-95",
    era: "anni90",
    roles: ["DIF"],
    positionTags: ["centrale", "braccetto"],
    country: "Serbia",
    score: 91,
    tag: "Sinistro velenoso",
    note: "Centrale mancino con punizioni da scegliere anche per stile.",
  },
  {
    id: "seedorf-sam-95",
    name: "Clarence Seedorf",
    club: "Sampdoria",
    season: "1995-96",
    era: "anni90",
    roles: ["CEN"],
    positionTags: ["mezzala", "mediano"],
    country: "Paesi Bassi",
    score: 90,
    tag: "Talento europeo",
    note: "Giovane centrocampista gia pronto per alzare il ritmo.",
  },
  {
    id: "flachi-sam-04",
    name: "Francesco Flachi",
    club: "Sampdoria",
    season: "2004-05",
    era: "anni2000",
    roles: ["ATT"],
    positionTags: ["centravanti", "seconda-punta"],
    country: "Italia",
    score: 90,
    goals: 14,
    tag: "Marassi nights",
    note: "Seconda punta cult della Samp europea di Novellino.",
  },
];

export const players: PlayerSeason[] = [...curatedPlayers, ...sourcedSerieA2022Players];
